import type { SampleEstimate } from '@/types/custom-playbooks';
import type { AcceptedPlayReviewRequest } from '@/containers/agents/use-accepted-play-review.hook';
import type { RequirementReviewInput } from '@/components/agent-chat/question-card-input-context';
import { markAgentSetupStarted } from '@/components/agent-chat/agent-setup-started';
import {
  paidAccessNotice,
  requestPaidAccess,
  type PaidAccessNotice,
} from '@/containers/settings/billing/paid-access';
import {
  parseContextMeter,
  type ContextMeter,
} from '@/components/agent-chat/context-meter.util';
import { withMentionIds } from '@/components/agent-chat/clu-mentions.util';
import { agentFilterCommandOf } from '@/components/agent-chat/list-filters.util';
import {
  CLU_MODEL_CHANGE_FAILED,
  CLU_MODEL_FLAG,
  CLU_MODEL_ROUTER_VARIANT,
  cluAnsweredModelOf,
  cluModelPickId,
  cluModelPickOf,
  saveConversationModelPick,
  type CluAnsweredModel,
  type CluModelPick,
} from '@/components/agent-chat/model-picker/clu-models';
import {
  cluModelDefaultsSaved,
  cluNewChatPick,
  scopeCluModelDefaults,
  useCluNewChatPick,
} from '@/components/agent-chat/model-picker/clu-model-defaults.store';
import { CONTINUE_CALL_ID } from '@/components/agent-chat/paused-turn.util';
import {
  turnPlayDraft,
  whenProposalRendered,
  type TurnProgress,
} from '@/components/agent-chat/play-draft.util';
import type { ChatImage } from '@/components/agent-chat/screenshot-attachment';
import { stopServerTurn } from '@/components/agent-chat/running-turn.util';
import {
  AUTO_RETRY_DELAYS_MS,
  CONNECTION_ENDED_MESSAGE,
  GATEWAY_STATUSES,
  isConnectionFailure,
  waitBeforeRetry,
} from '@/components/agent-chat/chat-send-retry.util';
import {
  forgetTabRunningTurn,
  rememberTabRunningTurn,
  takeTabRunningTurn,
} from '@/components/agent-chat/tab-running-turn.util';
import { useChatMessageArrival } from '@/hooks/use-chat-message-arrival.hook';
import { getApiBase } from '@/lib/api-base';
import { parseServerDate } from '@/lib/parse-server-date';
import { deferReload } from '@/lib/stale-chunk-reload';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchWebtokenHeadless } from '@/store/auth-helper';
import { extensionApi } from '@/store/features/extension.feature';
import {
  openCreditGate,
  selectAiChatConversationId,
  selectAiChatState,
  setAgentApplyFilters,
  setAiChatConversationId,
  setAiChatSessionState,
  setAiChatState,
  type SerializedAgentMessage,
  type SerializedChatState,
} from '@/store/features/ui.feature';
import { useAuth, useUser } from '@clerk/react';
import { usePostHog } from 'posthog-js/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import {
  chatTitleFromMessage,
  dropChat,
  forgetChat,
  markSent,
  NO_LOCAL_CHATS,
  orphanChat,
  pendingKey,
  resolveChat,
  settleChats,
  startChat,
  type LocalChats,
} from './local-chats.util';

// NOTE: cache-refresh after a Copilot mutation is NO LONGER driven from the SSE
// stream's display.type. The BE per-operation service now emits the canonical
// `lead_list_update` WebSocket event (with affected_list_ids) on every list
// mutation, and `use-unified-websocket.hook.ts` is the SOLE refresh authority —
// it invalidates {LeadListLeads,id} + {LeadListCompanies,id} + {LeadList,id} +
// {LeadList,'LIST'} for every touched list, independent of which list is open.
// The two hand-maintained display-type allow-list Sets (and the hand-listed
// list-id key array) that used to live here were the root cause of the
// drift-on-every-new-op hazard and have been deleted. display.type is now a
// pure presentational render hint (see copilot-tool-uis.tsx).

export type AgentContext = {
  type: 'general' | 'lead_list' | 'agent' | 'sequence';
  lead_list_id?: string;
  list_name?: string;
  lead_count?: number;
  /** The agent open in its workspace, for `type: 'agent'`. */
  agent_id?: string;
  agent_name?: string;
  /** The sequence open in the builder, for `type: 'sequence'`. */
  sequence_id?: string;
  sequence_name?: string;
  /**
   * The member's IANA time zone, which the request adds
   * (`withMemberTimeZone`): a schedule Clu sets with none named starts there.
   */
  timezone?: string;
};

/** The longest time zone name the API takes. */
const MAX_TIME_ZONE_LENGTH = 64;

/**
 * The context with the member's IANA time zone from the browser
 * ("Europe/Berlin"), so a schedule Clu sets with none named starts in it
 * rather than in UTC. The context as it was when the browser gives none.
 */
export function withMemberTimeZone<C extends object>(
  context: C
): C & { timezone?: string } {
  let zone: unknown;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    zone = undefined;
  }
  if (typeof zone !== 'string' || !zone || zone.length > MAX_TIME_ZONE_LENGTH)
    return context;
  return { ...context, timezone: zone };
}

/**
 * What a chat request says about its context beside `context` itself: an
 * agent's workspace names the agent, so the turn can be scoped to it.
 * Nothing for any other context, whose requests stay as they were.
 */
export function contextRequestFields(
  context: AgentContext
): Record<string, string> {
  if (context.type !== 'agent' || !context.agent_id) return {};
  return { context_type: 'agent', agent_id: context.agent_id };
}

export type ToolResultData = {
  id: string;
  name: string;
  result: Record<string, unknown>;
  /** The render hint plus a card's own fields (questions, options). */
  display: { type?: string; [key: string]: unknown };
  input_args?: Record<string, unknown>;
  /**
   * How much of the reply's text had arrived when this result did (live
   * messages only), so the Gumloop style can put Clu's narration between the
   * steps it came between.
   */
  textOffset?: number;
  /**
   * What the call was doing, in words built on the server from its arguments
   * ("Searching people: VP Sales, Fintech, United States"). The server sends
   * it on `tool_start` and `tool_result` and saves it with the result; none
   * for a tool without one.
   */
  caption?: string;
  /**
   * How long the call ran, from its `tool_start` to its result (live
   * messages only: a reopened chat has no times yet).
   */
  durationMs?: number;
};

/** The longest caption kept: the server sends at most 120 characters. */
const MAX_CAPTION_LENGTH = 200;

/** A `caption` from the stream as one line, or undefined for none. */
export function captionOf(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const line = value.replace(/\s+/g, ' ').trim();
  if (!line) return undefined;
  return line.length > MAX_CAPTION_LENGTH
    ? `${line.slice(0, MAX_CAPTION_LENGTH - 3).trimEnd()}...`
    : line;
}

export type DisambiguationList = {
  id: string;
  name: string;
  lead_count: number;
  created_at: string | null;
};

export type ConfirmationRequest = {
  sample?: SampleEstimate;
  id: string;
  /** Stable identity for retries of this confirmed-action turn. */
  clientTurnId?: string;
  /** Stable identity for retries of this cancellation turn. */
  cancelClientTurnId?: string;
  type?: string;
  tool_name: string;
  arguments: Record<string, unknown>;
  description: string;
  /** The card's own title, when the server names it (a connection write). */
  title?: string;
  credits_required?: number;
  credits_available?: number;
  expires_at?: string;
  // Server-minted single-use token (Billable-op SSOT). Echoed back in
  // confirmed_args so the backend can claim the action exactly once; a
  // replayed confirm re-presents the same token and is rejected at claim.
  action_token?: string;
  lists?: DisambiguationList[];
};

export type TryConfirmationIdentity = {
  actionToken: string;
  quoteId: string;
};

/** The card a Confirm click came from: its action and token. A queued
 *  handler of an older card never confirms the pending one. */
export type ConfirmationIdentity = {
  action?: string;
  actionToken?: string;
};

/** A tool Clu started whose result hasn't arrived: a live step row. */
export type RunningTool = {
  id: string;
  name: string;
  /** How much of the reply's text had arrived when it started. */
  textOffset: number;
  /** What the call is doing, in words (see ToolResultData.caption). */
  caption?: string;
  /** When its `tool_start` arrived (ms since epoch). */
  startedAt?: number;
};

/** Where in ``running`` the tool ``result`` answers sits: its id, else the
 *  first of its name; -1 for none. */
function runningIndexOf(
  running: readonly RunningTool[] | undefined,
  result: { id: string; name: string }
): number {
  if (!running?.length) return -1;
  const index = running.findIndex((tool) => tool.id === result.id);
  return index >= 0
    ? index
    : running.findIndex((tool) => tool.name === result.name);
}

/** The running tool ``result`` answers: its id, else the first of its name. */
export function runningToolFor(
  running: readonly RunningTool[] | undefined,
  result: { id: string; name: string }
): RunningTool | undefined {
  const at = runningIndexOf(running, result);
  return at < 0 ? undefined : running?.[at];
}

/** ``running`` less the tool ``result`` answers: its id, else its name. */
export function withoutRunning(
  running: RunningTool[] | undefined,
  result: { id: string; name: string }
): RunningTool[] | undefined {
  const at = runningIndexOf(running, result);
  return at < 0 || !running ? running : running.filter((_tool, i) => i !== at);
}

/**
 * A stretch Clu spent thinking in a live reply, from `thinking_start` to
 * `thinking_end`, shown as "Thought for 2.3s" among the steps where it
 * happened. A reopened chat has none yet.
 */
export type ThinkingSpell = {
  /** When it started (ms since epoch). */
  startedAt: number;
  /** How long it lasted, once it ended; undefined while it goes on. */
  durationMs?: number;
  /** How much of the reply's text had arrived when it started. */
  textOffset: number;
  /** How many tool results had arrived when it started. */
  afterResults: number;
};

/** ``spells`` with a new one started at ``at``, unless one is still open. */
export function startThinking(
  spells: readonly ThinkingSpell[],
  at: number,
  textOffset: number,
  afterResults: number
): ThinkingSpell[] {
  if (spells.some((spell) => spell.durationMs === undefined))
    return [...spells];
  return [...spells, { startedAt: at, textOffset, afterResults }];
}

/** ``spells`` with the open one ended at ``at``. */
export function endThinking(
  spells: readonly ThinkingSpell[],
  at: number
): ThinkingSpell[] {
  return spells.map((spell) =>
    spell.durationMs === undefined
      ? { ...spell, durationMs: Math.max(0, at - spell.startedAt) }
      : spell
  );
}

export type AgentMessage = {
  userInput?: RequirementReviewInput;
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Stable idempotency identity for this user-authored logical turn. */
  clientTurnId?: string;
  /** A Continue the chat sent itself after a pause, not typed by the user:
   *  the thread shows it as a small marker, not a message of theirs. */
  autoContinue?: boolean;
  /** Set by the thread on a reply the chat went on from by itself: its recap
   *  list folds away, the next turn carries on from it. */
  continuedOn?: boolean;
  /** When the workspace's daily AI allowance resets, on the reply that said
   *  it was used up: the reply shows the time in the viewer's own zone. */
  resetsAt?: string;
  /** A live reply the turn ended on an error, whatever it had to show (its
   *  recap and Continue reply included): that Continue is not a pause's. The
   *  server keeps no such mark, so a reopened chat reads without it. */
  endedInError?: boolean;
  thinking?: string;
  toolResults?: ToolResultData[];
  /** Tools still working in a live reply; each result replaces its row. */
  runningTools?: RunningTool[];
  /** The stretches Clu spent thinking in a live reply, in order. */
  thoughts?: ThinkingSpell[];
  confirmationRequest?: ConfirmationRequest;
  timestamp: Date;
  /** When a live reply started and finished streaming (ms since epoch). */
  startedAt?: number;
  completedAt?: number;
  /** When the server took a live reply's turn (its `accepted`, ms since
   *  epoch): the steps line's clock runs from it. Null on a reply this tab
   *  sent until then, while the user's message is sending. */
  acceptedAt?: number | null;
  /** The server's status while Clu works (`thinking` labels, a stall
   *  notice among them): the wait row's label in the agent workspace. */
  statusLabel?: string;
  /** clu_model_v1 router: the model that answered. */
  model?: CluAnsweredModel;
  /** A user message sent with a screenshot (the image itself is not kept):
   *  live, or reloaded from the saved message's mark. A reloaded chat shows
   *  the saved text, "Screenshot" alone when the user typed nothing, which
   *  also marks a message saved before the mark existed. */
  screenshot?: boolean;
  /** Who sent a user message, from the server; in a shared chat it can be a
   *  teammate. Unset on a message sent live here. */
  authorUserId?: string;
};

/** What the API saves for a screenshot sent with no words. */
export const SCREENSHOT_MESSAGE = 'Screenshot';

/** The user's text as the server saves it: their words, or "Screenshot"
 *  for an image sent without any. */
function savedUserText(message: string, image?: ChatImage): string {
  return image && !message.trim() ? SCREENSHOT_MESSAGE : message;
}

/**
 * The newest answer, named as `model`'s: the stream names the model that
 * answers a turn when it starts, and again when it changes.
 */
export function withAnswerModel(
  messages: AgentMessage[],
  model: CluAnsweredModel
): AgentMessage[] {
  const last = messages[messages.length - 1];
  if (last?.role !== 'assistant' || last.model === model) return messages;
  return [...messages.slice(0, -1), { ...last, model }];
}

/**
 * The display of the line the server posts into an agent's chat after the
 * user accepts, discards or saves its play on the canvas.
 */
const PLAY_EVENT_DISPLAY = 'play_event';

/**
 * The display of the line the server posts into an agent's chat when one of
 * its live runs fails (CLE-478): the diagnosis, with its quick replies.
 */
const RUN_DIAGNOSIS_DISPLAY = 'run_diagnosis';

/**
 * True for a message that is only such a line: a play event, or a failed
 * run's diagnosis with its quick replies. Neither answers a card waiting
 * above it.
 */
function isPlayEventLine(message: AgentMessage): boolean {
  const results = message.toolResults ?? [];
  if (
    message.role !== 'assistant' ||
    message.confirmationRequest ||
    results.length === 0
  )
    return false;
  if (results.every((result) => result.display?.type === PLAY_EVENT_DISPLAY))
    return true;
  return (
    results.some((result) => result.display?.type === RUN_DIAGNOSIS_DISPLAY) &&
    results.every(
      (result) =>
        result.display?.type === RUN_DIAGNOSIS_DISPLAY ||
        result.display?.type === 'quick_replies'
    )
  );
}

/**
 * The approval a reopened chat still waits on: the card on its last message,
 * read past the lines posted after Accept, Discard or a save on the canvas.
 * Those don't answer the card, so its token is still good on the server; any
 * other message after it does. Null for none.
 */
export function restoredConfirmationOf(
  messages: readonly AgentMessage[]
): ConfirmationRequest | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (isPlayEventLine(message)) continue;
    return message.role === 'assistant'
      ? (message.confirmationRequest ?? null)
      : null;
  }
  return null;
}

export type ConversationSummary = {
  id: string;
  title: string | null;
  context_type: string;
  context_lead_list_id: string | null;
  created_at: string;
  modified_at: string;
  message_count: number;
  /** When its owner shared it with the organization; null or absent while
   *  it is theirs alone (clu_chat_sharing_v1). */
  shared_at?: string | null;
};

type AgentChatState = {
  messages: AgentMessage[];
  isStreaming: boolean;
  error: string | null;
  conversationId: string | null;
  awaitingConfirmation: boolean;
  pendingConfirmation: ConfirmationRequest | null;
  /** How full the prompt of the chat's latest turn was; null before one. */
  contextMeter: ContextMeter | null;
  /** The play Clu is writing in the running turn ("step 3 of 5"). */
  progress: TurnProgress | null;
  /** clu_model_v1 router: the user's model for this chat, or Auto. A new
   *  chat's pick is held here until its first turn names the chat. */
  modelPick: CluModelPick;
};

/**
 * A chat as it stands, to set aside and put back as it was: an agent's
 * workspace sets the general chat aside for the agent's own chats and puts
 * it back on the way out.
 */
export type ChatSnapshot = Pick<
  AgentChatState,
  | 'messages'
  | 'conversationId'
  | 'awaitingConfirmation'
  | 'pendingConfirmation'
  | 'contextMeter'
> &
  Partial<Pick<AgentChatState, 'modelPick'>>;

function hasPendingRetryCard(messages: readonly AgentMessage[]): boolean {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role === 'user') return false;
    if (
      message.confirmationRequest ||
      message.toolResults?.some(
        (tool) => tool.display?.type === 'question_card'
      )
    )
      return true;
  }
  return false;
}

/** A chat's pick saves in flight (see savePick). */
type PickSaves = {
  /** Settles when the last save asked for has been dealt with. */
  tail: Promise<unknown>;
  /** How many saves have been asked for: the newest is the one that counts. */
  asked: number;
  /** The pick the server last kept. */
  kept: CluModelPick;
};

export type FailedTextTurnRetry = { id: string; messageId: string };

type FailedTextTurn = FailedTextTurnRetry & {
  message: string;
  /** The turn's screenshot, so Retry resends it under the same turn id. */
  image?: ChatImage;
  clientTurnId: string;
  conversationId: string | null;
  scope: string;
  epoch: number;
  generation: number;
  organizationId: string;
  context: AgentContext;
};

export type SendOptions = {
  /** Server-verified accepted proposal check, never an ordinary user prompt. */
  acceptedPlayReview?: AcceptedPlayReviewRequest;
  userInput?: RequirementReviewInput;
  expectedConversationId?: string;
  /** Private sample handoffs pin the originating workspace through auth waits. */
  ownedContext?: {
    agentId: string;
    organizationId: string;
    isCurrent: () => boolean;
    signal: AbortSignal;
  };
  confirmedAction?: string;
  confirmedArgs?: Record<string, unknown>;
  cancelledAction?: string;
  cancelledArgs?: { action_token: string; reason?: string };
  skipUserMessage?: boolean;
  clientTurnId?: string;
  /** Exclude this logical turn and anything after it from new-chat history. */
  historyBeforeMessageId?: string;
  /** Continue sent by the chat itself after a pause, not typed by the user:
   *  it goes on the same user message's count (`MAX_AUTO_CONTINUES`). */
  autoContinue?: boolean;
  /** A screenshot for this turn only; `message` may be empty. */
  image?: ChatImage;
};

/**
 * Result of a send. `ok` is false when the request failed (error SSE event or
 * a thrown fetch/stream error) — callers like confirmAction use this to know a
 * billable confirm did NOT go through and the confirmation card must be
 * re-shown so the user can retry. An aborted stream is reported separately
 * (`aborted: true`) and must NOT trigger a retry — the user cancelled it.
 */
export type SendResult = {
  ok: boolean;
  aborted?: boolean;
  /** Stable server error code when the failure has actionable semantics. */
  errorCode?: string;
  /** Accepted-review admission can retry; attempted terminal results cannot. */
  retryable?: boolean;
  /**
   * On a confirm turn: the stream delivered the confirmed action's result, so
   * the action ran (and any charge landed) even though the turn then failed
   * or was stopped. Only Clu's step after the action is what did not finish.
   */
  actionRan?: boolean;
  /**
   * On a confirm turn: the approval was claimed, or a dropped stream could
   * not confirm it is still unclaimed. The card must stay closed.
   */
  approvalClosed?: boolean;
};

/**
 * How a turn ended, which `isStreaming` dropping cannot tell apart: it
 * finished, it stopped on a confirmation card, it was stopped (Stop, a chat
 * switch, a send that took over) or it failed (an error, a cost limit).
 */
export type TurnOutcome =
  | 'done'
  | 'awaiting_confirmation'
  | 'stopped'
  | 'failed';

/**
 * The latest turn to end, and the chat it ran in (null when it never got an
 * id). `seq` rises with every turn, so the same outcome twice is two records.
 * Never persisted or snapshotted.
 */
export type LastTurn = {
  seq: number;
  outcome: TurnOutcome;
  conversationId: string | null;
  /** The turn stopped at one of its own limits (`turn_paused`). */
  pause?: TurnPause;
  /** The turn was the read-only review of an accepted or saved play: never
   *  continued by the chat, which would widen it to a full chat turn. */
  review?: boolean;
};

/**
 * A turn that stopped at a limit of its own (the allowance for one request,
 * its tool calls, the credits of its tree): not a failure. `autoContinue` says
 * Continue is enough to go on, so the chat sends it; false for the workspace's
 * daily AI allowance, which only a reset clears (`message` and `resetsAt`).
 */
export type TurnPause = {
  reason: string;
  autoContinue: boolean;
  message?: string;
  resetsAt?: string;
};

/** What the chat sends to pick a paused turn up (the server offers the same). */
export const CONTINUE_MESSAGE = 'Continue';
/** The most times the chat sends Continue itself for one user message. */
export const MAX_AUTO_CONTINUES = 3;

/** The `turn_paused` event's payload, or null when it is not one. */
export function turnPauseOf(payload: unknown): TurnPause | null {
  if (!payload || typeof payload !== 'object') return null;
  const data = payload as Record<string, unknown>;
  if (typeof data.reason !== 'string' || !data.reason) return null;
  return {
    reason: data.reason,
    // Only a stated true continues by itself: a missing flag never loops.
    autoContinue: data.auto_continue === true,
    ...(typeof data.message === 'string' && data.message
      ? { message: data.message }
      : {}),
    ...(typeof data.resets_at === 'string' && data.resets_at
      ? { resetsAt: data.resets_at }
      : {}),
  };
}

const APPROVAL_RESTART_REQUIRED = 'approval_restart_required';
const APPROVAL_RESTART_REQUIRED_MESSAGE =
  'This approval has expired or changed. Ask Clu to restart it.';

class AgentChatRequestError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly paidAccess?: PaidAccessNotice | null,
    readonly httpStatus?: number,
    readonly retryable?: boolean
  ) {
    super(message);
    this.name = 'AgentChatRequestError';
  }
}

async function responseError(
  response: Response
): Promise<AgentChatRequestError> {
  let errorCode: string | undefined;
  let detailMessage: string | undefined;
  let retryable: boolean | undefined;
  try {
    const payload = (await response.json()) as {
      error?: unknown;
      detail?: {
        error?: unknown;
        code?: unknown;
        message?: unknown;
        retryable?: unknown;
      } | null;
    };
    const notice = paidAccessNotice({ data: payload });
    if (notice)
      return new AgentChatRequestError(
        notice.message,
        undefined,
        notice,
        response.status
      );
    const text = (value: unknown) =>
      typeof value === 'string' && value ? value : undefined;
    const wrapped =
      payload.error && typeof payload.error === 'object'
        ? (payload.error as {
            message?: unknown;
            details?: typeof payload.detail;
          })
        : null;
    const detail =
      typeof payload.detail === 'object' ? payload.detail : wrapped?.details;
    retryable =
      typeof detail?.retryable === 'boolean' ? detail.retryable : undefined;
    errorCode =
      text(payload.error) ?? text(detail?.error) ?? text(detail?.code);
    // The API's error envelope moves `detail.message` up to `error.message`
    // ("Messages can be up to 50,000 characters…"), so a 4xx's own words
    // showed as "Clu couldn't finish this". A bare code ("BAD_REQUEST") or a
    // 5xx's text is not written for the user.
    const envelopeMessage = text(wrapped?.message);
    detailMessage =
      text(detail?.message) ??
      (response.status < 500 &&
      envelopeMessage &&
      !/^[A-Z_]+$/.test(envelopeMessage)
        ? envelopeMessage
        : undefined);
  } catch {
    // Preserve the existing status fallback for non-JSON upstream responses.
  }

  return new AgentChatRequestError(
    errorCode === APPROVAL_RESTART_REQUIRED
      ? APPROVAL_RESTART_REQUIRED_MESSAGE
      : (detailMessage ?? `HTTP ${response.status}`),
    errorCode,
    undefined,
    response.status,
    retryable
  );
}

/**
 * Whether a failed ordinary text turn may offer Retry. Retry re-sends the same
 * client_turn_id, so a turn the server already ran replays its stored result.
 * A network error or a gateway 5xx can mean the first request never reached
 * the app, and then the retry runs the turn for the first time.
 */
function canRecoverTextTurn(error: unknown): boolean {
  if (!(error instanceof AgentChatRequestError)) return true;
  if (error.httpStatus !== undefined) {
    return [408, 429, 500, 502, 503, 504].includes(error.httpStatus);
  }
  // Only an ACTIVE duplicate can later yield a different canonical result.
  // Terminal error streams replay unchanged, even if internally retryable.
  return error.code === 'turn_in_progress';
}

/** Convert AgentMessage[] to Redux-serializable form (Date → ISO string) */
function serializeMessages(msgs: AgentMessage[]): SerializedAgentMessage[] {
  return msgs.map((m) => ({
    ...m,
    timestamp:
      m.timestamp instanceof Date
        ? m.timestamp.toISOString()
        : (m.timestamp as unknown as string),
  }));
}

/** Convert serialized messages back to AgentMessage[] (ISO string → Date) */
function deserializeMessages(msgs: SerializedAgentMessage[]): AgentMessage[] {
  return msgs.map(({ model, ...m }) => {
    const answered = cluAnsweredModelOf(model);
    return {
      ...m,
      ...(answered ? { model: answered } : {}),
      timestamp: new Date(m.timestamp),
    };
  });
}

type AgentFilterCommand = NonNullable<
  Parameters<typeof setAgentApplyFilters>[0]
>;

/** A conversation message as the server stores it. */
export type StoredMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  thinking?: string;
  tool_results?: ToolResultData[];
  confirmation_request?: ConfirmationRequest;
  client_turn_id?: string | null;
  user_input?: RequirementReviewInput | null;
  auto_continue?: boolean | null;
  /** A user message sent with a screenshot, from its saved metadata (the
   *  image is not kept), returned the way `auto_continue` is. */
  screenshot?: boolean | null;
  /** Who sent a user message (the chat's owner, or a teammate in a shared
   *  chat). */
  author_user_id?: string | null;
  model?: string | null;
  created_at: string;
};

/** The line the server ends a saved reply with when its turn was stopped
 *  (Stop, or a server shutting down) before it finished. */
const INTERRUPTED_NOTE = /I was interrupted before I could finish\.[^\n]*$/;

/** The Continue a saved reply that was stopped early ends with, in every
 *  chat: the failed-turn Continue a reply that ended on an error offers. */
const INTERRUPTED_CONTINUE: ToolResultData = {
  id: CONTINUE_CALL_ID,
  name: 'offer_replies',
  result: { options: [CONTINUE_MESSAGE], __fromHistory: true },
  display: { type: 'quick_replies', options: [CONTINUE_MESSAGE] },
};

/** A saved reply that was stopped early, with its Continue; others as they are. */
function withInterruptedContinue(message: AgentMessage): AgentMessage {
  if (
    message.role !== 'assistant' ||
    !INTERRUPTED_NOTE.test(message.content.trimEnd()) ||
    message.toolResults?.some((tool) => tool.display?.type === 'quick_replies')
  )
    return message;
  return {
    ...message,
    toolResults: [...(message.toolResults ?? []), INTERRUPTED_CONTINUE],
    endedInError: true,
  };
}

/** The server's interrupted note in full, for a turn that never saved it. */
const INTERRUPTED_REPLY =
  'I was interrupted before I could finish. Press Continue to finish it, or send a new message.';

/**
 * A chat that ends on the user's message while the server runs no turn
 * (`turnRunning` false): the turn was cut before it could save its
 * interrupted note (its process was killed by a deploy, a crash or running
 * out of memory). It ends with that note and its Continue, as a turn that
 * saved the note does.
 */
function withCutTurnReply(
  messages: AgentMessage[],
  turnRunning: unknown
): AgentMessage[] {
  const last = messages.at(-1);
  if (turnRunning !== false || last?.role !== 'user') return messages;
  return [
    ...messages,
    withInterruptedContinue({
      id: `${last.id}-interrupted`,
      role: 'assistant',
      content: INTERRUPTED_REPLY,
      timestamp: last.timestamp,
    }),
  ];
}

/**
 * The chat right after Stop: the reply so far (or none) ends with the
 * interrupted note and its Continue, as the server saves it. Without it the
 * stopped turn showed nothing until the chat was reopened.
 */
function withStoppedNote(messages: AgentMessage[]): AgentMessage[] {
  const last = messages.at(-1);
  if (!last) return messages;
  if (last.role === 'user') {
    return [
      ...messages,
      withInterruptedContinue({
        id: `${last.id}-interrupted`,
        role: 'assistant',
        content: INTERRUPTED_REPLY,
        timestamp: new Date(),
      }),
    ];
  }
  if (INTERRUPTED_NOTE.test(last.content.trimEnd()) || last.confirmationRequest)
    return messages;
  const text = last.content.trimEnd();
  return [
    ...messages.slice(0, -1),
    withInterruptedContinue({
      ...last,
      runningTools: undefined,
      content: text ? `${text}\n\n${INTERRUPTED_REPLY}` : INTERRUPTED_REPLY,
    }),
  ];
}

const storedMessageAsIs = (m: StoredMessage): AgentMessage => ({
  id: m.id,
  role: m.role,
  content: m.content,
  clientTurnId: m.client_turn_id ?? undefined,
  ...(m.auto_continue === true ? { autoContinue: true } : {}),
  ...(m.screenshot === true ? { screenshot: true } : {}),
  ...(m.author_user_id ? { authorUserId: m.author_user_id } : {}),
  userInput: m.user_input ?? undefined,
  model: cluAnsweredModelOf(m.model) ?? undefined,
  thinking: m.thinking,
  toolResults:
    m.tool_results?.map((tr) => {
      // Tag every history-loaded result so side-effectful surfaces
      // (e.g. PeopleSearchHandoffSurface) know it's a replay, not a
      // live stream, and suppress auto-actions like navigation —
      // otherwise reopening/reloading the chat would yank the user to
      // /search for an old people_search.
      const displayType = tr.display?.type;
      // Guard against a stored result that is null / non-object —
      // spreading it directly would throw and break conversation
      // restore.
      const baseResult =
        tr.result && typeof tr.result === 'object'
          ? (tr.result as Record<string, unknown>)
          : {};
      const result: Record<string, unknown> = {
        ...baseResult,
        __fromHistory: true,
      };
      // Today's progress card shows a reloaded run completed: no
      // WebSocket replays its updates. The agents look's run row reads
      // __fromHistory instead and asks the run's task where it is, so
      // a run still going on reload never reads as finished.
      if (
        displayType === 'smart_column_started' ||
        displayType === 'enrichment_started'
      ) {
        result.status = 'completed';
      }
      return { ...tr, result };
    }) || undefined,
  confirmationRequest: m.confirmation_request || undefined,
  timestamp: new Date(m.created_at),
});

/**
 * A stored message as the chat shows it. Shared by opening a conversation and
 * by a message the server saved on its own (a slow lookup's result), so both
 * read a message the same way.
 */
export const storedMessageToAgentMessage = (m: StoredMessage): AgentMessage =>
  withInterruptedContinue(storedMessageAsIs(m));

/** How often a chat whose turn the server still runs is read again. */
const TURN_RUNNING_POLL_MS = 3000;

/** How long a turn's progress event waits for its proposal to show. */
const PROPOSAL_RENDER_WAIT_MS = 10_000;

/**
 * The status after a `thinking` label: a label that starts a sentence
 * ("The model stopped responding. Trying again...") replaces the one before;
 * a server's extension of it ("." per poll, " Done.") is added to it.
 */
function withStatusLabel(
  current: string | undefined,
  label: string
): string | undefined {
  if (!label.trim()) return current;
  return current && !/^[\p{L}\p{N}]/u.test(label)
    ? current + label
    : label.trim();
}

/**
 * The reply a reopened chat shows while its turn still runs, with one event
 * of the turn's replayed stream applied (P5): its text, its status line and
 * its step rows. The saved reply replaces it when the turn ends.
 */
export function withReplayedEvent(
  message: AgentMessage,
  type: string,
  data: Record<string, unknown>
): AgentMessage {
  if (type === 'text' && typeof data.content === 'string' && !data.error)
    return { ...message, content: message.content + data.content };
  // Taken long before the chat opened: the clock keeps the turn's start.
  if (type === 'accepted')
    return message.acceptedAt
      ? message
      : { ...message, acceptedAt: message.startedAt ?? Date.now() };
  if (type === 'thinking_start')
    return { ...message, thinking: '', statusLabel: undefined };
  if (type === 'thinking' && typeof data.content === 'string')
    return {
      ...message,
      thinking: (message.thinking ?? '') + data.content,
      statusLabel: withStatusLabel(message.statusLabel, data.content),
    };
  if (type === 'thinking_end') return { ...message, statusLabel: undefined };
  if (type === 'play_draft' && typeof data.label === 'string' && data.label)
    return { ...message, statusLabel: data.label };
  const id = String(data.id ?? '');
  const name = String(data.name ?? '');
  const caption = captionOf(data.caption);
  if (type === 'tool_start')
    return {
      ...message,
      runningTools: [
        ...(message.runningTools ?? []),
        {
          id,
          name,
          textOffset: message.content.length,
          ...(caption ? { caption } : {}),
        },
      ],
    };
  if (type === 'tool_result') {
    const started = runningToolFor(message.runningTools, { id, name });
    const shown = caption ?? started?.caption;
    return {
      ...message,
      runningTools: withoutRunning(message.runningTools, { id, name }),
      // A play draft's label ends with its tool (no thinking_end follows).
      statusLabel: undefined,
      toolResults: [
        ...(message.toolResults ?? []),
        {
          id,
          name,
          result: (data.result as Record<string, unknown>) ?? {},
          display: (data.display as ToolResultData['display']) ?? {},
          textOffset: message.content.length,
          ...(shown ? { caption: shown } : {}),
          ...(typeof data.duration_ms === 'number' && data.duration_ms >= 0
            ? { durationMs: data.duration_ms }
            : {}),
        },
      ],
    };
  }
  return message;
}

/**
 * A turn whose stream dropped is looked for in its chat this many times, a
 * second further apart each time, while the connection is still down: a
 * blink of the browser's network, or a deploy restarting the API, passes in
 * a few seconds (CLE-565).
 */
const FOLLOW_READS = 3;
const FOLLOW_BACKOFF_MS = 1000;

/** A chat as the server reads it: its saved messages and whether a turn is
 *  still working in it. */
type SavedChatRead = {
  turn_running?: boolean;
  running_turn_id?: unknown;
  messages?: StoredMessage[];
  context_meter?: unknown;
  last_turn_outcome?: unknown;
};

/**
 * A failed send whose turn the server may hold all the same: the stream
 * dropped or never answered (the browser's own `TypeError`, or a stream that
 * ended before its reply), the gateway answered for an API that was
 * restarting, or a retry found the turn still running. A refusal the server
 * wrote (an `error` event or a 4xx) is final and is not looked for.
 */
function mayStillRun(error: unknown): boolean {
  if ((error as Error | undefined)?.name === 'AbortError') return false;
  if (!(error instanceof AgentChatRequestError)) return true;
  if (error.httpStatus !== undefined)
    return GATEWAY_STATUSES.includes(error.httpStatus);
  return error.code === 'turn_in_progress';
}

/**
 * A send that got no answer at all: the browser's connection error, a stream
 * that ended before its first byte, or a gateway 502/503/504. The chat sends
 * it again by itself (CLE-565). A refusal the server wrote (a 4xx, a 402 for
 * credits, an `error` event) and a Stop never go again.
 */
function sentNothing(error: unknown): boolean {
  if (error instanceof AgentChatRequestError)
    return (
      error.httpStatus !== undefined &&
      GATEWAY_STATUSES.includes(error.httpStatus)
    );
  return isConnectionFailure(error);
}

/** The chat's last turn had not finished when it was put aside: a user
 *  message with no reply, or a live reply that never ended. */
function endsMidTurn(messages: SerializedAgentMessage[]): boolean {
  const last = messages[messages.length - 1];
  if (!last) return false;
  return last.role === 'user' || (!!last.startedAt && !last.completedAt);
}

/**
 * How a reopened chat's turn ended once the server reads it ended. A card
 * waits first. Then the server's `last_turn_outcome`: a failed turn saves its
 * error as a plain reply, so its text reads as done. With none (an older API,
 * or a turn it cannot name), the saved chat says: a reply stopped early
 * (also a turn cut before it saved any reply, which `withCutTurnReply` ends
 * with the interrupted note), no reply at all (failed), or done.
 */
function reopenedTurnOutcome(
  messages: AgentMessage[],
  awaitingConfirmation: boolean,
  serverOutcome: unknown
): TurnOutcome {
  if (awaitingConfirmation) return 'awaiting_confirmation';
  if (
    serverOutcome === 'done' ||
    serverOutcome === 'failed' ||
    serverOutcome === 'stopped'
  )
    return serverOutcome;
  const last = messages[messages.length - 1];
  if (last?.role !== 'assistant') return 'failed';
  return last.endedInError ? 'stopped' : 'done';
}

export type UseAgentChatOptions = {
  /** Current org already resolved by the shared provider. Retry scope only. */
  organizationId?: string;
  /** Persist under this session's own key. Omitted (the primary session)
   *  keeps aiChatState/aiChatConversationId. */
  sessionKey?: string;
  /** False while this session streams in the background: agent UI commands
   *  (apply_filters) are held and applied when it becomes active again. */
  isActive?: boolean;
};

export const useAgentChat = (
  context: AgentContext = { type: 'general' },
  { sessionKey, isActive = true, organizationId }: UseAgentChatOptions = {}
) => {
  const posthog = usePostHog();
  const { user } = useUser();
  const { orgId, userId } = useAuth();
  const historyScope = JSON.stringify([orgId, userId]);
  const dispatch = useAppDispatch();
  const persistedConversationId = useAppSelector((s) =>
    sessionKey
      ? (s.ui.aiChatSessionStates?.[sessionKey]?.conversationId ?? undefined)
      : selectAiChatConversationId(s)
  );
  const persistedChatState = useAppSelector((s) =>
    sessionKey ? s.ui.aiChatSessionStates?.[sessionKey] : selectAiChatState(s)
  );
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const heldFilterCommandRef = useRef<AgentFilterCommand | null>(null);

  useEffect(() => {
    if (!isActive || !heldFilterCommandRef.current) return;
    dispatch(setAgentApplyFilters(heldFilterCommandRef.current));
    heldFilterCommandRef.current = null;
  }, [dispatch, isActive]);

  // Restore lastUserMessageRef from persisted state so confirmAction works after remount
  const lastUserMessageRef = useRef<string>(
    persistedChatState?.lastUserMessage ?? ''
  );

  // A new chat no one has chosen a model for follows the member's default:
  // a blank one put back from the store too, unless its pick was chosen.
  const [followsDefault] = useState(
    () =>
      !persistedChatState ||
      (persistedChatState.messages.length === 0 &&
        !persistedConversationId &&
        !persistedChatState.modelPickChosen)
  );
  const [state, setState] = useState<AgentChatState>(() => {
    // Restore full chat state from Redux — instant, no API call
    if (persistedChatState && persistedChatState.messages.length > 0) {
      return {
        messages: deserializeMessages(persistedChatState.messages),
        isStreaming: false,
        error: null,
        conversationId: persistedChatState.conversationId,
        awaitingConfirmation: persistedChatState.awaitingConfirmation,
        pendingConfirmation: persistedChatState.pendingConfirmation ?? null,
        contextMeter: parseContextMeter(persistedChatState.contextMeter),
        progress: null,
        modelPick: cluModelPickOf(persistedChatState.modelPick),
      };
    }
    return {
      messages: [],
      isStreaming: false,
      error: null,
      conversationId: persistedConversationId ?? null,
      awaitingConfirmation: false,
      pendingConfirmation: null,
      contextMeter: null,
      progress: null,
      // A new chat starts on the member's default (the starred row).
      modelPick: followsDefault
        ? cluNewChatPick()
        : cluModelPickOf(persistedChatState?.modelPick),
    };
  });

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyFailed, setHistoryFailed] = useState(false);
  const historyEpoch = useRef({ scope: historyScope });
  if (historyEpoch.current.scope !== historyScope)
    historyEpoch.current = { scope: historyScope };
  const historyOrigin = historyEpoch.current;
  const historyMounted = useRef(true);

  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const loadingConversationRef = useRef(false);
  // The chats the lists don't show yet: see local-chats.util.
  const [localChats, setLocalChats] = useState<LocalChats>(NO_LOCAL_CHATS);
  const [lastTurn, setLastTurn] = useState<LastTurn | null>(null);
  // The server's code for the failure state.error shows, while it shows it
  // (credits_held: the chat offers Buy credits beside it, CLE-567).
  const [failure, setFailure] = useState<{
    message: string;
    code: string;
  } | null>(null);
  const turnSeqRef = useRef(0);
  // The user message whose turns the chat may continue by itself, and how many
  // times it has, per chat and message (`MAX_AUTO_CONTINUES`).
  const continuedMessageRef = useRef('');
  const autoContinuesRef = useRef(new Map<string, number>());

  const abortControllerRef = useRef<AbortController | null>(null);
  // The turn this chat shows running, for Stop: the latest send's, or the
  // one a reopened chat reads from the server (`running_turn_id`). Stop names
  // it, so a late Stop never ends a later turn of the chat.
  const liveTurnIdRef = useRef<string | null>(null);
  // The play draft of the turn this chat follows on the server, which Stop
  // takes off the canvas at once (the watch reads on until the turn ends).
  const watchedDraftsRef = useRef<ReturnType<typeof turnPlayDraft> | null>(
    null
  );
  // clu_model_v1 router: each chat's pick saves that are waiting or running.
  const pickSavesRef = useRef(new Map<string, PickSaves>());
  // clu_model_v1 router: each chat's pick as the server last reported it (on
  // load, in a turn's `model` event, or by a save it kept).
  const serverPicksRef = useRef(new Map<string, CluModelPick>());
  // How many times the user has changed a pick, to tell whether they chose
  // while a turn was on its way.
  const pickChangesRef = useRef(0);
  // pickChangesRef when the chat was last made new: equal while the user has
  // not chosen in it, so it follows the member's default and its first turn
  // sends no model (the server starts it on the star). A chat put back from
  // the store with a chosen pick, or with turns, keeps that pick.
  const newChatPicksRef = useRef(followsDefault ? 0 : -1);
  // Whether the user chose this chat's pick, kept with the chat in the store.
  const [pickChosen, setPickChosen] = useState(!followsDefault);
  const notePickChosen = useCallback(
    () => setPickChosen(pickChangesRef.current !== newChatPicksRef.current),
    []
  );
  const retryTurnIdsRef = useRef(new Map<string, string>());
  const confirmationTurnIdsRef = useRef(new Map<string, string>());
  const cancellationTurnIdsRef = useRef(new Map<string, string>());
  const chatGenerationRef = useRef(0); // Increment on clearChat to ignore stale callbacks
  useLayoutEffect(() => {
    historyMounted.current = true;
    return () => {
      historyMounted.current = false;
      chatGenerationRef.current += 1;
      abortControllerRef.current?.abort();
    };
  }, []);
  const contextRef = useRef(context);
  contextRef.current = context;
  const stateRef = useRef(state);
  stateRef.current = state;
  // Ephemeral authority for one failed ordinary text turn, never serialized.
  const [failedTextTurn, setFailedTextTurn] = useState<FailedTextTurn | null>(
    null
  );
  const failedTextRef = useRef<FailedTextTurn | null>(null);
  const retryAttemptRef = useRef<FailedTextTurn | null>(null);
  const retryControllerRef = useRef<
    | ({ controller: AbortController } & (
        | { epoch: number }
        | { scopeEpoch: number }
      ))
    | null
  >(null);
  // Who is chatting about what. The context's display fields (an agent's name,
  // a list's name and count) change under a running turn, and a change there
  // must not take Retry away.
  const scope = JSON.stringify([
    user?.id ?? null,
    organizationId ?? null,
    sessionKey ?? null,
    context.type,
    context.agent_id ?? null,
    context.lead_list_id ?? null,
    context.sequence_id ?? null,
  ]);
  const retryScopeRef = useRef({
    scope,
    active: isActive,
    epoch: 0,
    // Counts changes of `scope` alone: a chat tab moving to the background
    // or back keeps it, unlike `epoch`.
    scopeEpoch: 0,
    organizationId,
  });
  // At commit, never in render: a render React discards must not revoke the
  // Retry the committed UI holds. The render that changes scope hides it.
  useLayoutEffect(() => {
    if (
      retryScopeRef.current.scope !== scope ||
      retryScopeRef.current.active !== isActive
    ) {
      failedTextRef.current = null;
      retryAttemptRef.current = null;
      retryScopeRef.current = {
        scope,
        active: isActive,
        epoch: retryScopeRef.current.epoch + 1,
        scopeEpoch:
          retryScopeRef.current.scopeEpoch +
          (retryScopeRef.current.scope !== scope ? 1 : 0),
        organizationId,
      };
    }
  }, [scope, isActive, organizationId]);
  useEffect(() => {
    const active = retryControllerRef.current;
    if (
      active &&
      ('scopeEpoch' in active
        ? active.scopeEpoch !== retryScopeRef.current.scopeEpoch
        : active.epoch !== retryScopeRef.current.epoch)
    )
      active.controller.abort();
  }, [scope, isActive]);

  useEffect(() => {
    // StrictMode replays effect setup. Only a mounted, active runtime may
    // retain Retry authority; ordinary background sends keep their semantics.
    retryScopeRef.current.active = isActiveRef.current;
    return () => {
      failedTextRef.current = null;
      retryAttemptRef.current = null;
      retryScopeRef.current.active = false;
      retryScopeRef.current.epoch += 1;
      retryControllerRef.current?.controller.abort();
    };
  }, []);

  const forgetTextRetry = useCallback(() => {
    failedTextRef.current = null;
    retryAttemptRef.current = null;
    setFailedTextTurn(null);
  }, []);

  // Callers that read the list in the same task (the provider, this hook and
  // the sidebar's lists as a turn ends) share one request. A later call
  // reads again, so a turn that ended since is never missed.
  const conversationsReadRef = useRef<Promise<void> | null>(null);
  const historyReadOrigin = useRef(historyOrigin);
  if (historyReadOrigin.current !== historyOrigin) {
    historyReadOrigin.current = historyOrigin;
    conversationsReadRef.current = null;
    setConversations([]);
    setLocalChats(NO_LOCAL_CHATS);
    setHistoryFailed(false);
    setIsLoadingHistory(false);
  }
  const fetchConversations = useCallback((): Promise<void> => {
    if (
      !historyMounted.current ||
      historyEpoch.current !== historyOrigin ||
      !orgId ||
      !userId
    )
      return Promise.resolve();
    if (conversationsReadRef.current) return conversationsReadRef.current;
    const active = () =>
      historyMounted.current && historyEpoch.current === historyOrigin;
    const read = (async () => {
      setIsLoadingHistory(true);
      try {
        const token = await fetchWebtokenHeadless();
        if (!active()) return;
        const res = await fetch(`${getApiBase()}/api/v1/agent/conversations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Could not load chat history');
        {
          const data = await res.json();
          if (!Array.isArray(data.conversations))
            throw new Error('Could not load chat history');
          if (!active()) return;
          const listed: ConversationSummary[] = data.conversations;
          setHistoryFailed(false);
          setConversations(listed);
          // In the same update, so a started chat hands over to its listed
          // row without a frame where both or neither show.
          setLocalChats((prev) =>
            settleChats(prev, new Set(listed.map((chat) => chat.id)))
          );
        }
      } catch {
        if (active()) setHistoryFailed(true);
      } finally {
        if (active()) setIsLoadingHistory(false);
      }
    })();
    conversationsReadRef.current = read;
    const release = () => {
      if (conversationsReadRef.current === read)
        conversationsReadRef.current = null;
    };
    void read.then(release, release);
    return read;
  }, [historyOrigin, orgId, userId]);

  /**
   * A read of the agents' chats, `agentIds`, came back holding `listed`: a
   * started agent's chat hands over to its listed row in the same update.
   * Clu's list never holds one, so its read leaves them to this.
   */
  const settleAgentChats = useCallback(
    (listed: ReadonlySet<string>, agentIds: ReadonlySet<string>) => {
      setLocalChats((prev) => settleChats(prev, listed, agentIds));
    },
    []
  );

  /**
   * The chat as the server saved it once its turn ended, and that end
   * recorded as a streamed turn's is, so what the user queued while it ran
   * goes out or waits. Returns how the turn ended.
   */
  const showEndedTurn = useCallback(
    (conversationId: string, data: SavedChatRead): TurnOutcome => {
      const messages = withCutTurnReply(
        (data.messages || []).map(storedMessageToAgentMessage),
        data.turn_running
      );
      const restoredConfirmation = restoredConfirmationOf(messages);
      setState((prev) => ({
        ...prev,
        messages,
        isStreaming: false,
        error: null,
        awaitingConfirmation: !!restoredConfirmation,
        pendingConfirmation: restoredConfirmation,
        contextMeter: parseContextMeter(data.context_meter),
      }));
      turnSeqRef.current += 1;
      // The turn's reserve has settled: the balance reads again.
      dispatch(extensionApi.util.invalidateTags(['Credits']));
      const outcome = reopenedTurnOutcome(
        messages,
        !!restoredConfirmation,
        data.last_turn_outcome
      );
      setLastTurn({ seq: turnSeqRef.current, outcome, conversationId });
      return outcome;
    },
    [dispatch]
  );

  /**
   * Reads chat `conversationId` every few seconds while the server still runs
   * its turn, then shows the saved reply. Ends when the chat moves on (a
   * send, another chat, a new one) or the conversation can't be read. The
   * turn's end is recorded as a streamed turn's is, so what the user queued
   * while it ran goes out or waits; `onEnded` hears how it ended. `drafts`
   * is the play draft of a send whose stream dropped, which the replay
   * carries on, so its canvas keeps the draft rather than flashing the saved
   * play until the replay draws it again.
   */
  const watchRunningTurn = useCallback(
    async (
      conversationId: string,
      generation: number,
      onEnded?: (outcome: TurnOutcome) => void,
      drafts: ReturnType<typeof turnPlayDraft> = turnPlayDraft(dispatch)
    ) => {
      const current = () =>
        historyMounted.current && chatGenerationRef.current === generation;
      const endTurn = (outcome: TurnOutcome) => {
        turnSeqRef.current += 1;
        // The turn's reserve has settled: the balance reads again.
        dispatch(extensionApi.util.invalidateTags(['Credits']));
        setLastTurn({ seq: turnSeqRef.current, outcome, conversationId });
      };
      watchedDraftsRef.current = drafts;
      // What the turn sent before the chat opened, then the rest as it
      // comes, until it ends; the saved reply is read after.
      const replayed = await (async () => {
        try {
          const token = await fetchWebtokenHeadless();
          const res = await fetch(
            `${getApiBase()}/api/v1/agent/conversations/${conversationId}/turn-stream`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const reader = res.ok ? res.body?.getReader() : undefined;
          if (!reader) return false;
          const decoder = new TextDecoder();
          let buffer = '';
          let type = '';
          let ended = false;
          for (;;) {
            const { done, value } = await reader.read();
            if (!current()) {
              void reader.cancel().catch(() => undefined);
              return false;
            }
            if (done) return ended;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (line.startsWith('event: ')) {
                type = line.slice(7).trim();
                continue;
              }
              if (!line.startsWith('data: ')) continue;
              const raw = line.slice(6);
              if (raw === '[DONE]') {
                ended = true;
                continue;
              }
              let data: Record<string, unknown>;
              try {
                data = JSON.parse(raw);
              } catch {
                continue;
              }
              const event = type;
              // The play it was writing, on the canvas as it stands.
              const progress =
                event === 'play_draft' ? drafts.draft(data) : null;
              const draftEnded = event === 'tool_result' && drafts.result(data);
              setState((prev) => {
                const msgs = [...prev.messages];
                const last = msgs[msgs.length - 1];
                if (last?.role !== 'assistant') return prev;
                msgs[msgs.length - 1] = withReplayedEvent(last, event, data);
                return {
                  ...prev,
                  messages: msgs,
                  ...(progress ? { progress } : {}),
                  ...(draftEnded ? { progress: null } : {}),
                };
              });
            }
          }
        } catch {
          return false; // the chat reads the conversation instead
        }
      })();
      let wait = !replayed;
      try {
        for (;;) {
          if (wait)
            await new Promise((done) => setTimeout(done, TURN_RUNNING_POLL_MS));
          wait = true;
          if (!current()) return;
          let data: SavedChatRead;
          try {
            const token = await fetchWebtokenHeadless();
            const res = await fetch(
              `${getApiBase()}/api/v1/agent/conversations/${conversationId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!current()) return;
            if (!res.ok) {
              setState((prev) => ({ ...prev, isStreaming: false }));
              endTurn('failed');
              return;
            }
            data = await res.json();
          } catch {
            continue; // offline for a moment: read again
          }
          if (!current()) return;
          if (data.turn_running === true) continue;
          const outcome = showEndedTurn(conversationId, data);
          onEnded?.(outcome);
          return;
        }
      } finally {
        // The turn ended, or the chat moved on: a draft it never handed
        // over goes.
        drafts.end();
        if (watchedDraftsRef.current === drafts)
          watchedDraftsRef.current = null;
      }
    },
    [dispatch, showEndedTurn]
  );

  /**
   * A send whose stream dropped, or whose retry found the turn still running:
   * the server may hold the turn all the same (it runs whether or not its
   * stream reaches the browser), so its chat is read. Still running there,
   * the chat follows it to its saved reply; ended with this turn saved, the
   * chat shows what the server saved, which for a turn a deploy cut is the
   * interrupted note and its Continue. False when the chat holds no such turn
   * or cannot be read, and the send fails as before, with its Retry. A Stop
   * (`signal`) ends the reads, so the send ends as a stop. `reads` is how
   * many times the chat is read (one, before a send goes again by itself).
   * `onEnded` hears how the turn ended, once the chat shows its saved reply.
   * `drafts`, the send's play draft, goes on with the turn followed.
   */
  const followTurnOnServer = useCallback(
    async (
      conversationId: string,
      clientTurnId: string,
      generation: number,
      signal: AbortSignal,
      reads: number = FOLLOW_READS,
      onEnded?: (outcome: TurnOutcome) => void,
      drafts?: ReturnType<typeof turnPlayDraft>
    ): Promise<boolean> => {
      const current = () =>
        historyMounted.current &&
        chatGenerationRef.current === generation &&
        !signal.aborted;
      for (let attempt = 0; attempt < reads; attempt += 1) {
        if (attempt > 0)
          await new Promise((done) =>
            setTimeout(done, FOLLOW_BACKOFF_MS * attempt)
          );
        if (!current()) return false;
        let data: SavedChatRead;
        try {
          const token = await fetchWebtokenHeadless();
          const res = await fetch(
            `${getApiBase()}/api/v1/agent/conversations/${conversationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // The gateway answering for an API that is restarting: read again.
          if (GATEWAY_STATUSES.includes(res.status)) continue;
          if (!res.ok) return false;
          data = await res.json();
        } catch {
          continue; // the connection is still down: read again
        }
        if (!current()) return false;
        if (data.turn_running === true) {
          // Another turn of the chat (another tab's) is not this one.
          if (
            typeof data.running_turn_id === 'string' &&
            data.running_turn_id !== clientTurnId
          )
            return false;
          setState((prev) => {
            // The server runs it: taken, whether or not the stream said so.
            const last = prev.messages.at(-1);
            const messages =
              last?.role === 'assistant'
                ? [
                    ...prev.messages.slice(0, -1),
                    withReplayedEvent(last, 'accepted', {}),
                  ]
                : prev.messages;
            // The replay below says what it is writing now.
            return {
              ...prev,
              messages,
              isStreaming: true,
              error: null,
              progress: null,
            };
          });
          void watchRunningTurn(conversationId, generation, onEnded, drafts);
          return true;
        }
        const saved = (data.messages ?? []).some(
          (m) => m.role === 'user' && m.client_turn_id === clientTurnId
        );
        if (data.turn_running !== false || !saved) return false;
        const outcome = showEndedTurn(conversationId, data);
        onEnded?.(outcome);
        return true;
      }
      return false;
    },
    [showEndedTurn, watchRunningTurn]
  );

  const loadConversation = useCallback(
    async (conversationId: string) => {
      if (!historyMounted.current) return;
      forgetTextRetry();
      loadingConversationRef.current = true;
      const generation = ++chatGenerationRef.current;
      abortControllerRef.current?.abort();
      liveTurnIdRef.current = null;
      setLocalChats((prev) => orphanChat(prev));
      setIsLoadingConversation(true);
      setState((prev) => ({
        ...prev,
        error: null,
      }));
      try {
        const token = await fetchWebtokenHeadless();
        if (!historyMounted.current || chatGenerationRef.current !== generation)
          return;
        const res = await fetch(
          `${getApiBase()}/api/v1/agent/conversations/${conversationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (chatGenerationRef.current !== generation) return;
        if (!res.ok) {
          setState((prev) => ({
            ...prev,
            error: 'Unable to open that conversation.',
          }));
          return;
        }
        const data = await res.json();
        if (chatGenerationRef.current !== generation) return;

        const messages: AgentMessage[] = withCutTurnReply(
          (data.messages || []).map(storedMessageToAgentMessage),
          data.turn_running
        );

        // Detect if the restored conversation ends with a pending confirmation
        const restoredConfirmation = restoredConfirmationOf(messages);

        // Restore lastUserMessageRef to the most recent user message
        const lastUserMsg = [...messages]
          .reverse()
          .find((m) => m.role === 'user');
        if (lastUserMsg) {
          lastUserMessageRef.current = lastUserMsg.content;
        }

        // clu_model_v1 router: the router's view of the chat, absent off (or
        // when the server's flag read failed). Remembered as the server's
        // either way, so a turn does not send the Auto shown for an absent one.
        const modelPick = cluModelPickOf(data.model_router?.pick);
        serverPicksRef.current.set(conversationId, modelPick);
        // A turn the server is still running here (it goes on when the page
        // leaves): Clu shows working until its reply is saved.
        const turnRunning = data.turn_running === true;
        liveTurnIdRef.current =
          turnRunning && typeof data.running_turn_id === 'string'
            ? data.running_turn_id
            : null;
        // Working since the message that started the turn, not since the
        // chat opened (CLE-565).
        const asked = messages.at(-1);
        const askedAt =
          asked?.role === 'user' ? asked.timestamp.getTime() : Number.NaN;
        setState({
          messages: turnRunning
            ? [
                ...messages,
                {
                  id: `assistant-${Date.now()}`,
                  role: 'assistant',
                  content: '',
                  toolResults: [],
                  timestamp: new Date(),
                  startedAt:
                    Number.isFinite(askedAt) && askedAt <= Date.now()
                      ? askedAt
                      : Date.now(),
                },
              ]
            : messages,
          isStreaming: turnRunning,
          error: null,
          conversationId,
          awaitingConfirmation: !!restoredConfirmation,
          pendingConfirmation: restoredConfirmation,
          contextMeter: parseContextMeter(data.context_meter),
          progress: null,
          modelPick,
        });
        if (turnRunning) void watchRunningTurn(conversationId, generation);
      } catch {
        if (chatGenerationRef.current !== generation) return;
        setState((prev) => ({
          ...prev,
          error: 'Unable to open that conversation.',
        }));
      } finally {
        if (chatGenerationRef.current === generation) {
          loadingConversationRef.current = false;
          setIsLoadingConversation(false);
        }
      }
    },
    [forgetTextRetry, watchRunningTurn]
  );

  // A chat put back from memory whose turn was still streaming when it was
  // set aside: the server went on with it, so read what it saved.
  const restoredMidTurnRef = useRef(
    !!persistedChatState?.conversationId &&
      endsMidTurn(persistedChatState.messages)
  );
  useEffect(() => {
    if (!restoredMidTurnRef.current) return;
    restoredMidTurnRef.current = false;
    const id = stateRef.current.conversationId;
    if (id) void loadConversation(id);
  }, [loadConversation]);

  // -------------------------------------------------------------------------
  // Auto-load conversation when ID is set but messages are empty
  // (e.g., resuming an onboarding copilot thread after redirect).
  // Watches both local state and the Redux-dispatched persistedConversationId.
  // -------------------------------------------------------------------------
  const autoLoadedConvRef = useRef<string | null>(null);
  useEffect(() => {
    // Determine the target ID — prefer local state, fall back to Redux
    const targetId = state.conversationId || persistedConversationId || null;
    if (
      targetId &&
      targetId !== autoLoadedConvRef.current &&
      state.messages.length === 0 &&
      !state.isStreaming &&
      !isLoadingHistory
    ) {
      autoLoadedConvRef.current = targetId;
      loadConversation(targetId);
    }
  }, [
    state.conversationId,
    state.messages.length,
    state.isStreaming,
    persistedConversationId,
    isLoadingHistory,
    loadConversation,
  ]);

  // A reload (a deploy's stale-chunk reload too) empties the store. So the
  // general chat keeps the chat it is streaming in the tab: the primary
  // session, or an extra one while shown, never an Agent's (its chat is in the
  // URL). After the reload the primary opens it and the turn_running read in
  // loadConversation takes over.
  const holdsTabTurn = context.type !== 'agent' && (!sessionKey || isActive);
  const heldTabTurnRef = useRef<string | null>(null);
  const holdTabTurn = useCallback((id: string | null) => {
    const held = heldTabTurnRef.current;
    if (id === held) return;
    if (held) forgetTabRunningTurn(held);
    if (id) rememberTabRunningTurn(id);
    heldTabTurnRef.current = id;
  }, []);
  useEffect(() => {
    holdTabTurn(
      holdsTabTurn && state.isStreaming ? state.conversationId : null
    );
  }, [holdTabTurn, holdsTabTurn, state.isStreaming, state.conversationId]);
  // A brand-new chat streams before run_started names it, so there is no id
  // to keep yet: a stale-chunk reload in that moment waits briefly for it.
  const awaitingTabTurnId =
    holdsTabTurn && state.isStreaming && !state.conversationId;
  useEffect(() => {
    if (!awaitingTabTurnId) return;
    return deferReload(() => true);
  }, [awaitingTabTurnId]);
  useEffect(() => {
    if (sessionKey || contextRef.current.type === 'agent') return;
    const id = takeTabRunningTurn();
    if (!id || persistedConversationId) return;
    setState((prev) =>
      prev.conversationId || prev.messages.length
        ? prev
        : { ...prev, conversationId: id }
    );
    // Once, on mount: what the tab held when it reloaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.conversationId || state.isStreaming) return;
    void fetchConversations();
  }, [fetchConversations, state.conversationId, state.isStreaming]);

  // A message the server saved into this conversation on its own, such as a
  // slow lookup's result, joins the chat as it lands.
  useChatMessageArrival({
    conversationId: state.conversationId,
    isStreaming: state.isStreaming,
    onMessage: (stored) => {
      const arrived = storedMessageToAgentMessage(stored as StoredMessage);
      setState((prev) => {
        if (prev.messages.some((m) => m.id === arrived.id)) return prev;
        // In time order, as a reload shows it: one held while a newer turn
        // streamed goes before that turn, not after it.
        const at = prev.messages.findIndex(
          (m) => m.timestamp > arrived.timestamp
        );
        const messages =
          at === -1
            ? [...prev.messages, arrived]
            : [
                ...prev.messages.slice(0, at),
                arrived,
                ...prev.messages.slice(at),
              ];
        return { ...prev, messages };
      });
    },
  });

  /** Deletes a Clu or agent chat; true when the server did. */
  const deleteConversation = useCallback(
    async (id: string): Promise<boolean> => {
      const origin = historyEpoch.current;
      const active = () =>
        historyMounted.current && historyEpoch.current === origin;
      if (!active()) return false;
      forgetTextRetry();
      try {
        const token = await fetchWebtokenHeadless();
        if (!active()) return false;
        const res = await fetch(
          `${getApiBase()}/api/v1/agent/conversations/${id}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
        if (!active() || !res.ok) return false;
        setConversations((prev) => prev.filter((c) => c.id !== id));
        setLocalChats((prev) => forgetChat(prev, id));
        // If it was the active conversation, clear chat
        if (stateRef.current.conversationId === id) {
          newChatPicksRef.current = pickChangesRef.current;
          notePickChosen();
          setState({
            messages: [],
            isStreaming: false,
            error: null,
            conversationId: null,
            awaitingConfirmation: false,
            pendingConfirmation: null,
            contextMeter: null,
            progress: null,
            modelPick: cluNewChatPick(),
          });
        }
        return true;
      } catch {
        return false;
      }
    },
    [forgetTextRetry, notePickChosen]
  );

  // The member's model settings are this organization's and user's.
  useEffect(() => {
    if (orgId && userId) scopeCluModelDefaults(historyScope);
  }, [orgId, userId, historyScope]);

  // clu_model_v1 router: a new chat the user has not chosen for shows the
  // member's default as it is read or starred.
  const newChatPick = useCluNewChatPick();
  useEffect(() => {
    const blank = (chat: AgentChatState) =>
      !chat.conversationId &&
      chat.messages.length === 0 &&
      !chat.isStreaming &&
      chat.modelPick !== newChatPick;
    if (pickChangesRef.current !== newChatPicksRef.current) return;
    if (!blank(stateRef.current)) return;
    stateRef.current = { ...stateRef.current, modelPick: newChatPick };
    setState((prev) =>
      blank(prev) ? { ...prev, modelPick: newChatPick } : prev
    );
  }, [newChatPick, state.conversationId, state.messages.length]);

  // -------------------------------------------------------------------------
  // Sync full chat state to Redux so it survives page navigations (instant)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const serialized: SerializedChatState = {
      messages: serializeMessages(state.messages),
      conversationId: state.conversationId,
      awaitingConfirmation: state.awaitingConfirmation,
      pendingConfirmation: state.pendingConfirmation,
      contextMeter: state.contextMeter,
      modelPick: state.modelPick,
      modelPickChosen: pickChosen || undefined,
      lastUserMessage: lastUserMessageRef.current || undefined,
    };
    if (sessionKey) {
      dispatch(
        setAiChatSessionState({ key: sessionKey, chatState: serialized })
      );
      return;
    }
    dispatch(setAiChatState(serialized));

    // Also keep conversation ID in sync for backward compat
    if (state.conversationId !== persistedConversationId) {
      dispatch(setAiChatConversationId(state.conversationId ?? undefined));
    }
  }, [
    state.messages,
    state.conversationId,
    state.awaitingConfirmation,
    state.pendingConfirmation,
    state.contextMeter,
    state.modelPick,
    pickChosen,
    persistedConversationId,
    dispatch,
    sessionKey,
  ]);

  /** Chat `id`'s picker moves from `from` to `to`, unless it moved on. */
  const movePick = useCallback(
    (id: string, from: CluModelPick, to: CluModelPick) => {
      const current = stateRef.current;
      if (current.conversationId === id && current.modelPick === from)
        stateRef.current = { ...current, modelPick: to };
      setState((prev) =>
        prev.conversationId === id && prev.modelPick === from
          ? { ...prev, modelPick: to }
          : prev
      );
    },
    []
  );

  /**
   * clu_model_v1 router: saves `pick` as chat `id`'s model through the
   * router's user-pick API. A chat's saves go out one at a time, in the order
   * they were asked for, so the server ends on the last pick; one still
   * waiting when a newer is asked for is dropped, as the server needs only
   * the last. When the server refuses a save, the chat goes back to the pick
   * it last kept (`held` before it has kept one) unless the picker has moved
   * on. True when the server kept the pick.
   */
  const savePick = useCallback(
    (id: string, pick: CluModelPick, held: CluModelPick): Promise<boolean> => {
      const current = () =>
        historyMounted.current && historyEpoch.current === historyOrigin;
      if (!current()) return Promise.resolve(false);
      const chats = pickSavesRef.current;
      const saves = chats.get(id) ?? {
        tail: Promise.resolve(),
        asked: 0,
        kept: held,
      };
      chats.set(id, saves);
      const mine = ++saves.asked;
      const saving = saves.tail.then(async (): Promise<boolean> => {
        if (!current()) return false;
        if (saves.asked !== mine) return true;
        const saved = await saveConversationModelPick(id, pick, current);
        if (!current()) return false;
        if (saved) {
          saves.kept = pick;
          serverPicksRef.current.set(id, pick);
        } else if (saves.asked === mine) {
          movePick(id, pick, saves.kept);
        }
        return saved;
      });
      const settled = saving.then(
        () => undefined,
        () => undefined
      );
      saves.tail = settled;
      // Idle: nothing left to order against.
      void settled.then(() => {
        if (saves.tail === settled) chats.delete(id);
      });
      return saving;
    },
    [movePick, historyOrigin]
  );

  const sendMessage = useCallback(
    async (
      message: string,
      options?: SendOptions,
      retry?: FailedTextTurn
    ): Promise<SendResult> => {
      if (!historyMounted.current || !orgId || !userId)
        return { ok: false, aborted: true };
      const acceptedReview = options?.acceptedPlayReview;
      if (acceptedReview) {
        if (
          message.trim() ||
          options?.userInput ||
          options?.confirmedAction ||
          options?.cancelledAction
        )
          return { ok: false, errorCode: 'accepted_review_invalid' };
        if (
          acceptedReview.organizationId !== orgId ||
          contextRef.current.type !== 'agent' ||
          contextRef.current.agent_id !== acceptedReview.agentId ||
          stateRef.current.conversationId !== acceptedReview.conversationId
        )
          return { ok: false, aborted: true };
        if (
          loadingConversationRef.current ||
          failedTextRef.current ||
          failedTextTurn?.scope === scope ||
          stateRef.current.isStreaming ||
          (stateRef.current.awaitingConfirmation &&
            !(
              stateRef.current.pendingConfirmation?.expires_at &&
              Date.parse(stateRef.current.pendingConfirmation.expires_at) <=
                Date.now()
            )) ||
          (abortControllerRef.current &&
            !abortControllerRef.current.signal.aborted)
        )
          return { ok: false, errorCode: 'conversation_busy', retryable: true };
      }
      const skipUserMessage = options?.skipUserMessage || !!acceptedReview;
      let namedRetryId: string | null = null;
      const ownsRetry = () =>
        !retry ||
        (retryAttemptRef.current === retry &&
          retryScopeRef.current.active &&
          retryScopeRef.current.scope === retry.scope &&
          retryScopeRef.current.epoch === retry.epoch &&
          (stateRef.current.conversationId === retry.conversationId ||
            (retry.conversationId === null &&
              namedRetryId !== null &&
              stateRef.current.conversationId === namedRetryId)) &&
          !loadingConversationRef.current);
      if (!ownsRetry()) return { ok: false, aborted: true };
      if (!retry) forgetTextRetry();
      const sendScope = { ...retryScopeRef.current };

      if (options?.userInput && loadingConversationRef.current) {
        return { ok: false, errorCode: 'conversation_loading' };
      }
      // A streamed review card is actionable only after its producing turn settles.
      // Refuse before aborting the active stream or minting a new logical turn.
      if (options?.userInput && stateRef.current.isStreaming) {
        return { ok: false, errorCode: 'turn_in_progress' };
      }
      const owned = options?.ownedContext;
      const ownsContext = () =>
        (!options?.expectedConversationId ||
          stateRef.current.conversationId === options.expectedConversationId) &&
        (!options?.userInput || contextRef.current.type === 'agent') &&
        (!acceptedReview ||
          (contextRef.current.type === 'agent' &&
            contextRef.current.agent_id === acceptedReview.agentId &&
            historyMounted.current &&
            historyEpoch.current === historyOrigin)) &&
        (!owned ||
          (!owned.signal.aborted &&
            owned.isCurrent() &&
            contextRef.current.type === 'agent' &&
            contextRef.current.agent_id === owned.agentId));
      if (!ownsContext()) return { ok: false, aborted: true };
      // Block sends before onboarding — no org means the API will 401
      if (!user?.unsafeMetadata?.completed_onboarding) {
        setState((prev) => ({
          ...prev,
          error: 'Please complete onboarding before using Clu.',
        }));
        return { ok: false };
      }

      // The user's own message in an agent's workspace starts its setup: the
      // workspace swaps the guided screen for the panel. A confirm or cancel
      // click and a retry do not count.
      const sendContext = contextRef.current;
      if (
        sendContext?.type === 'agent' &&
        sendContext.agent_id &&
        !skipUserMessage &&
        !options?.confirmedAction &&
        !options?.cancelledAction
      ) {
        markAgentSetupStarted(sendContext.agent_id);
      }

      const clientTurnId = options?.clientTurnId ?? crypto.randomUUID();
      const userMessageId = retry?.messageId ?? `user-${Date.now()}`;
      // A message of the user's starts a count of its own; Continue the chat
      // sent itself, a retry, a confirm or a review stays on the message that
      // was paused.
      if (!options?.autoContinue && !skipUserMessage) {
        continuedMessageRef.current = userMessageId;
        autoContinuesRef.current.clear();
      }
      const ordinaryText =
        (!skipUserMessage || Boolean(retry)) &&
        !options?.userInput &&
        !options?.confirmedAction &&
        !options?.cancelledAction &&
        !options?.ownedContext;
      // A screenshot rides only an ordinary message (the API refuses it with
      // anything else); a retry carries the failed turn's own.
      const image =
        ordinaryText && !acceptedReview
          ? (options?.image ?? retry?.image)
          : undefined;
      const shownMessage = savedUserText(message, image);
      let terminalCompleted = false;
      let sawActionableCard = false;

      // Capture request context before the optimistic user message and
      // assistant placeholder are appended. Authentication can yield long
      // enough for React to commit that optimistic state, and reading the ref
      // afterward would echo the new prompt (plus an empty assistant turn)
      // back in a brand-new conversation's history.
      const requestMessages = stateRef.current.messages;
      const requestConversationId = stateRef.current.conversationId;
      const historyBoundary = options?.historyBeforeMessageId
        ? requestMessages.findIndex(
            (item) => item.id === options.historyBeforeMessageId
          )
        : -1;
      const historyMessages =
        historyBoundary >= 0
          ? requestMessages.slice(0, historyBoundary)
          : requestMessages;
      const conversationHistory = requestConversationId
        ? [] // Backend loads persisted history from DB
        : historyMessages.map((m) => ({
            role: m.role,
            content: m.content || '',
          }));

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      const abortOwned = () => controller.abort();
      owned?.signal.addEventListener('abort', abortOwned, { once: true });
      abortControllerRef.current = controller;
      if (retry)
        retryControllerRef.current = { epoch: retry.epoch, controller };
      const generation = ++chatGenerationRef.current;
      // A chat still opening is superseded by this send: its read is dropped,
      // so nothing else is left to end the loading it began.
      if (loadingConversationRef.current) {
        loadingConversationRef.current = false;
        setIsLoadingConversation(false);
      }

      // The sidebar and the Chats rail show the chat in this same tick: a
      // send with no chat starts one under a key of its own, and a send in
      // a chat moves it first.
      const chatKey = pendingKey(clientTurnId);
      const startsChat = !requestConversationId;
      const sentAt = Date.now();
      setLocalChats((prev) =>
        startsChat
          ? startChat(prev, {
              key: chatKey,
              title: chatTitleFromMessage(shownMessage),
              agentId:
                sendContext?.type === 'agent'
                  ? (sendContext.agent_id ?? null)
                  : null,
              at: sentAt,
            })
          : markSent(prev, requestConversationId, sentAt)
      );
      // The `router` variant of clu_model_v1 (the backend enforces it too):
      // on it, a turn can carry the chat's pick and the server applies it
      // with the turn.
      const routerOn =
        posthog?.getFeatureFlag(CLU_MODEL_FLAG) === CLU_MODEL_ROUTER_VARIANT;
      // How many pick changes there had been when the request went out.
      let pickChangesAtRequest = pickChangesRef.current;
      // The chat's id, from the first event that names it.
      let namedId: string | null = null;
      const nameChat = (id: string) => {
        if (acceptedReview && id !== acceptedReview.conversationId)
          throw new DOMException(
            'Accepted review named a different chat',
            'AbortError'
          );
        if (
          retry &&
          ((retry.conversationId !== null && id !== retry.conversationId) ||
            (namedRetryId !== null && id !== namedRetryId))
        ) {
          throw new DOMException('Retry named a different chat', 'AbortError');
        }
        if (namedId === id) return;
        namedId = id;
        if (chatGenerationRef.current !== generation) return;
        if (retry) namedRetryId = id;
        setState((prev) =>
          prev.conversationId === id ? prev : { ...prev, conversationId: id }
        );
        if (startsChat) setLocalChats((prev) => resolveChat(prev, chatKey, id));
      };
      // The router has routed this turn, and `held` is the pick the chat
      // holds after it. The user may have chosen again while the reply was
      // on its way (a save made before the turn was routed is overtaken by
      // the pick the turn carried): save the picker's model now, after it.
      // Otherwise the server's pick stands and the picker shows it: another
      // tab changed it, or the chat opened without the router's view.
      const reconcilePick = (held: CluModelPick) => {
        const chatId = namedId ?? requestConversationId;
        if (!chatId || chatGenerationRef.current !== generation) return;
        serverPicksRef.current.set(chatId, held);
        const shown = stateRef.current.modelPick;
        if (shown === held) return;
        if (pickChangesRef.current !== pickChangesAtRequest) {
          void savePick(chatId, shown, held).then((saved) => {
            if (!saved) toast.error(CLU_MODEL_CHANGE_FAILED);
          });
        } else if (!pickSavesRef.current.has(chatId)) {
          // A save still out settles the chat on the picker's model instead.
          movePick(chatId, shown, held);
        }
      };
      // How this turn ended, in the chat it ran in.
      const endTurn = (
        outcome: TurnOutcome,
        turnConversationId: string | null = namedId ?? requestConversationId,
        pause?: TurnPause | null
      ) => {
        turnSeqRef.current += 1;
        // The turn's reserve has settled: the balance reads again.
        dispatch(extensionApi.util.invalidateTags(['Credits']));
        setLastTurn({
          seq: turnSeqRef.current,
          outcome,
          conversationId: turnConversationId,
          ...(pause ? { pause } : {}),
          ...(acceptedReview ? { review: true } : {}),
        });
      };
      const stopped = (): SendResult => {
        if (retry && chatGenerationRef.current !== generation)
          return { ok: false, aborted: true };
        if (startsChat) setLocalChats((prev) => orphanChat(prev, chatKey));
        endTurn('stopped');
        return { ok: false, aborted: true };
      };
      // A confirm turn (not a list pick, which only re-asks): the server
      // claims the approval, runs the action and streams its result under
      // `tool-{action}` before Clu's step after it starts. Once that result
      // or any other one after run_started arrives, the action has run.
      const confirmedAction =
        options?.confirmedAction && !options.confirmedArgs?._disambiguation
          ? options.confirmedAction
          : null;
      let runStarted = false;
      let actionRan = false;
      // A confirm turn's first status ("Executing …") comes only after the
      // server claimed the approval's single-use token (agent_chat.py), so a
      // turn that fails or drops after it has spent the approval. An error
      // before it (the claim failed, the chat didn't load) has not. A dropped
      // stream needs a server read before treating the token as unclaimed.
      let approvalClaimed = false;
      const ran = (): Pick<SendResult, 'actionRan' | 'approvalClosed'> =>
        actionRan
          ? { actionRan }
          : confirmedAction && approvalClaimed
            ? { approvalClosed: true }
            : {};

      if (!skipUserMessage) {
        lastUserMessageRef.current = message;
        const userMessage: AgentMessage = {
          id: userMessageId,
          role: 'user',
          content: shownMessage,
          clientTurnId,
          ...(image ? { screenshot: true } : {}),
          ...(options?.userInput ? { userInput: options.userInput } : {}),
          ...(options?.autoContinue ? { autoContinue: true } : {}),
          timestamp: new Date(),
        };

        posthog?.capture('agent_chat_message_sent', {
          context_type: contextRef.current.type,
          lead_list_id: contextRef.current.lead_list_id,
          conversation_id: stateRef.current.conversationId,
          message_length: message.length,
          user_id: user?.id,
          ...(image ? { screenshot: true } : {}),
          ...(options?.autoContinue ? { auto_continue: true } : {}),
        });

        // Add an immediate assistant placeholder so the thinking indicator
        // renders on the very first paint (assistant-ui's optimistic message
        // mechanism fires in useEffect which is too late).
        const assistantPlaceholder: AgentMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          toolResults: [],
          timestamp: new Date(),
          startedAt: Date.now(),
          acceptedAt: null,
        };

        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, userMessage, assistantPlaceholder],
          isStreaming: true,
          error: null,
          awaitingConfirmation: false,
          pendingConfirmation: null,
          progress: null,
        }));
      } else {
        const assistantPlaceholder: AgentMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          toolResults: [],
          timestamp: new Date(),
          startedAt: Date.now(),
          acceptedAt: null,
        };

        setState((prev) => ({
          ...prev,
          messages: [
            ...(historyBoundary >= 0
              ? prev.messages.slice(0, historyBoundary + 1)
              : prev.messages),
            assistantPlaceholder,
          ],
          isStreaming: true,
          error: null,
          awaitingConfirmation: false,
          pendingConfirmation: null,
          progress: null,
        }));
      }

      // Drip state — declared outside try so the catch (abort) path can tear down
      let rafId: number | null = null;
      let displayedContent = '';
      let pendingText = '';
      let thinkingContent = '';
      // The stretches Clu spent thinking in this reply (see ThinkingSpell).
      let thoughts: ThinkingSpell[] = [];
      // The play Clu writes in this turn, drawn on its agent's canvas.
      const drafts = turnPlayDraft(dispatch);
      // How long after the send the server took the turn, first showed
      // anything (a status, a step or text) and first wrote text: sent when
      // the turn ends, here or followed on the server after its stream drops.
      let msToAccepted: number | null = null;
      let msToFirstProgress: number | null = null;
      let msToFirstText: number | null = null;
      const capture = (renderedAt: number | null) =>
        posthog?.capture('clu_turn_progress', {
          ms_to_accepted: msToAccepted,
          ms_to_first_progress: msToFirstProgress,
          ms_to_first_text: msToFirstText,
          ms_to_proposal_render:
            renderedAt === null ? null : renderedAt - sentAt,
          surface: 'portal',
        });

      const flushAndTeardown = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        displayedContent += pendingText;
        pendingText = '';
        // A thought still open when the turn ends ends with it.
        const thinkingOpen = thoughts.some(
          (spell) => spell.durationMs === undefined
        );
        if (thinkingOpen) thoughts = endThinking(thoughts, Date.now());
        const ended = thoughts;
        // A tool that never answered is not still working once the turn ends.
        if (chatGenerationRef.current === generation)
          setState((prev) => {
            const lastMsg = prev.messages[prev.messages.length - 1];
            const stillRunning = !!lastMsg?.runningTools?.length;
            const endsThought = thinkingOpen && lastMsg?.role === 'assistant';
            if (!stillRunning && !endsThought) return prev;
            const msgs = [...prev.messages];
            msgs[msgs.length - 1] = {
              ...lastMsg,
              runningTools: undefined,
              ...(endsThought ? { thoughts: ended } : {}),
            };
            return { ...prev, messages: msgs };
          });
      };

      let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
      let readerFinished = false;
      try {
        const token = await fetchWebtokenHeadless();
        if (chatGenerationRef.current !== generation) return stopped();
        if (!ownsContext() || !ownsRetry() || controller.signal.aborted)
          throw new DOMException('Workspace changed', 'AbortError');
        if (owned && requestConversationId) {
          // Verify the actual stored conversation before including a private sample.
          const response = await fetch(
            `${getApiBase()}/api/v1/agent/conversations/${encodeURIComponent(requestConversationId)}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Cleanlist-Expected-Org': owned.organizationId,
              },
              signal: controller.signal,
            }
          );
          if (!response.ok)
            throw new Error('Could not verify this agent chat.');
          const conversation = await response.json();
          if (
            conversation.context_type !== 'agent' ||
            conversation.context_agent_id !== owned.agentId
          )
            throw new Error(
              'Open this agent’s own chat before sharing the sample.'
            );
          if (
            !ownsContext() ||
            controller.signal.aborted ||
            chatGenerationRef.current !== generation ||
            stateRef.current.conversationId !== requestConversationId
          )
            throw new DOMException('Workspace changed', 'AbortError');
        }
        const url = `${getApiBase()}/api/v1/agent/chat`;

        const body: Record<string, unknown> = {
          message,
          // The agents and skills the message picked, and the member's time
          // zone, ride in its context.
          context: withMentionIds(
            withMemberTimeZone(retry?.context ?? contextRef.current),
            message
          ),
          ...contextRequestFields(retry?.context ?? contextRef.current),
          conversation_history: conversationHistory,
          conversation_id: requestConversationId,
          // Minted once for the user-authored logical turn and reused by the
          // retry path. The backend keys its receipt and idempotency fence on
          // copilot:turn:portal:{org}:{turn}.
          client_turn_id: clientTurnId,
        };
        // For this request only: history and saved rows stay text.
        if (image)
          body.image = { media_type: image.mediaType, data: image.data };
        // An Accept sends no turn id (the server runs it under its own), so
        // its Stop names the chat.
        liveTurnIdRef.current = acceptedReview ? null : clientTurnId;

        if (acceptedReview) {
          if (acceptedReview.kind === 'saved') {
            body.trigger = 'agent_play_saved';
            body.save_event_id = acceptedReview.saveEventId;
            body.play_id = acceptedReview.playId;
            body.play_version = acceptedReview.version;
          } else {
            body.trigger = 'agent_play_accepted';
            body.proposal_id = acceptedReview.proposalId;
          }
          delete body.client_turn_id;
          delete body.conversation_history;
        }
        if (options?.userInput) body.user_input = options.userInput;
        // The server keeps it with the message, so a reloaded chat shows the
        // Continue as the chat's own, not the user's.
        if (options?.autoContinue) body.auto_continue = true;
        // The pick as it stands as the request goes out, for a new chat or
        // one whose pick the server has not reported as this one (a save
        // still out); Auto goes as "auto", which clears a pick the chat
        // holds. A pick the server already holds is not sent: a picker that
        // shows a stale one (another tab's, or Auto for a chat opened without
        // the router's view) would overwrite the user's pick.
        // A new chat the user has not chosen for sends none: the server
        // starts it on the member's star, which the picker here may not
        // know yet (still being read, a failed read, another tab's change).
        // A preset or star changed just before is saved first: the server
        // routes the turn on what it holds (Max until Standard is saved).
        if (routerOn) await cluModelDefaultsSaved(controller.signal);
        if (routerOn && !acceptedReview) {
          const pick = stateRef.current.modelPick;
          pickChangesAtRequest = pickChangesRef.current;
          if (
            requestConversationId
              ? serverPicksRef.current.get(requestConversationId) !== pick
              : pickChangesRef.current !== newChatPicksRef.current
          )
            body.model = cluModelPickId(pick);
        }

        if (options?.confirmedAction) {
          body.confirmed_action = options.confirmedAction;
          body.confirmed_args = options.confirmedArgs;
        }
        if (options?.cancelledAction) {
          body.cancelled_action = options.cancelledAction;
          body.cancelled_args = options.cancelledArgs;
        }

        // A send that gets no answer at all goes again by itself (CLE-565):
        // at once, then after 2 seconds, with the same body and so the same
        // client_turn_id, which the server's execution fence runs and bills
        // once. Not an Accept (it carries no turn id), a confirm or cancel
        // (the server refuses a second claim of its card), or a send with a
        // private sample: those fail as before.
        const autoRetries =
          !acceptedReview &&
          !owned &&
          !options?.confirmedAction &&
          !options?.cancelledAction &&
          (ordinaryText || Boolean(options?.userInput));
        // Background sessions may finish their turn, resends included, but a
        // send put aside by a different workspace or context must never
        // start another attempt.
        const ownsAutoRetry = () =>
          historyMounted.current &&
          historyEpoch.current === historyOrigin &&
          retryScopeRef.current.scope === sendScope.scope &&
          retryScopeRef.current.scopeEpoch === sendScope.scopeEpoch;
        // The stream's first bytes, read here so a stream that ends before
        // them counts as no answer.
        let firstChunk: Uint8Array | null = null;
        for (let attempt = 0; ; attempt += 1) {
          if (
            !ownsRetry() ||
            (attempt > 0 && !ownsAutoRetry()) ||
            chatGenerationRef.current !== generation ||
            controller.signal.aborted
          )
            throw new DOMException('Chat changed', 'AbortError');
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...(owned || retry
                  ? {
                      'X-Cleanlist-Expected-Org':
                        owned?.organizationId ?? retry!.organizationId,
                    }
                  : {}),
              },
              body: JSON.stringify(body),
              signal: controller.signal,
            });

            if (
              chatGenerationRef.current !== generation ||
              !ownsRetry() ||
              controller.signal.aborted
            ) {
              void response.body?.cancel().catch(() => undefined);
              throw new DOMException('Chat changed', 'AbortError');
            }
            if (!response.ok) {
              throw await responseError(response);
            }

            reader = response.body?.getReader();
            if (!reader) throw new Error('No reader available');
            const first = await reader.read();
            if (first.done) {
              readerFinished = true;
              throw new Error(CONNECTION_ENDED_MESSAGE);
            }
            firstChunk = first.value;
            break;
          } catch (sendError) {
            if (
              !autoRetries ||
              attempt >= AUTO_RETRY_DELAYS_MS.length ||
              controller.signal.aborted ||
              !sentNothing(sendError)
            )
              throw sendError;
            // An automatic resend survives backgrounding, but loses its
            // controller on a real scope change. Manual Retry keeps its
            // stricter authority, which also ends when the tab is inactive.
            if (!retry)
              retryControllerRef.current = {
                scopeEpoch: sendScope.scopeEpoch,
                controller,
              };
            if (reader && !readerFinished)
              void reader.cancel().catch(() => undefined);
            reader = undefined;
            readerFinished = false;
            await waitBeforeRetry(
              AUTO_RETRY_DELAYS_MS[attempt],
              controller.signal
            );
            if (
              !ownsContext() ||
              !ownsRetry() ||
              !ownsAutoRetry() ||
              chatGenerationRef.current !== generation ||
              controller.signal.aborted
            )
              throw new DOMException('Chat changed', 'AbortError');
            // The request may have reached the server all the same: a chat
            // that holds this turn (running, or ended and saved) is followed
            // there, and the turn is not sent again.
            if (
              requestConversationId &&
              (await followTurnOnServer(
                requestConversationId,
                clientTurnId,
                generation,
                controller.signal,
                1
              ))
            )
              return { ok: false, aborted: true };
          }
        }
        if (!reader) throw new Error('No reader available');

        const decoder = new TextDecoder();
        let buffer = '';
        let assistantContent = '';
        const toolResults: ToolResultData[] = [];
        // The calls started and not answered yet, for each result's caption
        // and how long it ran.
        let startedCalls: RunningTool[] = [];
        let thinkingDirty = false;
        const progressed = () => {
          msToFirstProgress ??= Date.now() - sentAt;
        };
        // The reply's turn is taken: its `accepted`, or the run_started of an
        // API that sends none.
        const markAccepted = () => {
          const at = Date.now();
          setState((prev) => {
            const lastMsg = prev.messages.at(-1);
            if (lastMsg?.role !== 'assistant' || lastMsg.acceptedAt)
              return prev;
            return {
              ...prev,
              messages: [
                ...prev.messages.slice(0, -1),
                { ...lastMsg, acceptedAt: at },
              ],
            };
          });
        };
        // The thoughts as they stand, on the reply being written.
        const showThoughts = () => {
          const shown = thoughts;
          setState((prev) => {
            const msgs = [...prev.messages];
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg?.role !== 'assistant') return prev;
            msgs[msgs.length - 1] = { ...lastMsg, thoughts: shown };
            return { ...prev, messages: msgs };
          });
        };

        const drip = () => {
          rafId = null;
          // Bail if chat was cleared (new chat started)
          if (chatGenerationRef.current !== generation || !ownsRetry()) return;
          let updated = false;

          // Drip pending text word-by-word (or small chunk)
          if (pendingText.length > 0) {
            // Find the next word boundary (include trailing space/newline)
            const match = pendingText.match(/^\S+\s*|^\s+/);
            const chunk = match ? match[0] : pendingText;
            displayedContent += chunk;
            pendingText = pendingText.slice(chunk.length);
            updated = true;
          }

          if (updated || thinkingDirty) {
            const contentSnapshot = displayedContent;
            const thinkingSnapshot = thinkingContent;
            const needsThinking = thinkingDirty;
            thinkingDirty = false;
            setState((prev) => {
              const msgs = [...prev.messages];
              const lastMsg = msgs[msgs.length - 1];
              if (lastMsg?.role === 'assistant') {
                msgs[msgs.length - 1] = {
                  ...lastMsg,
                  content: contentSnapshot,
                  ...(needsThinking ? { thinking: thinkingSnapshot } : {}),
                };
              } else {
                msgs.push({
                  id: `assistant-${Date.now()}`,
                  role: 'assistant',
                  content: contentSnapshot,
                  toolResults: [],
                  timestamp: new Date(),
                  startedAt: Date.now(),
                });
              }
              return { ...prev, messages: msgs };
            });
          }

          // Keep dripping if there's more pending text
          if (pendingText.length > 0) {
            rafId = requestAnimationFrame(drip);
          }
        };

        const scheduleFlush = () => {
          if (rafId === null) {
            rafId = requestAnimationFrame(drip);
          }
        };
        let confirmationRequest: ConfirmationRequest | undefined;
        let conversationId: string | null = null;
        // clu_model_v1 router: the model answering this turn, once named.
        let turnModel: CluAnsweredModel | null = null;
        let pickReconciled = false;
        let awaitingConfirmation = false;
        // The turn stopped at a limit of its own (`turn_paused`): not an
        // error, so it ends like any finished turn.
        let pausedTurn: TurnPause | null = null;
        let streamDone = false;
        let currentEventType = '';

        while (!streamDone) {
          if (chatGenerationRef.current !== generation) break;
          const { done, value } = firstChunk
            ? { done: false as const, value: firstChunk }
            : await reader.read();
          firstChunk = null;
          if (
            chatGenerationRef.current !== generation ||
            !ownsRetry() ||
            controller.signal.aborted
          )
            throw new DOMException('Chat changed', 'AbortError');
          if (done) {
            readerFinished = true;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEventType = line.slice(7).trim();
              continue;
            }
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);
            if (data === '[DONE]') {
              terminalCompleted = true;
              streamDone = true;
              break;
            }

            try {
              const parsed = JSON.parse(data);

              // Thinking events
              if (currentEventType === 'thinking_start') {
                if (confirmedAction) approvalClaimed = true;
                thinkingContent = '';
                // Create/update assistant message with thinking state
                setState((prev) => {
                  const msgs = [...prev.messages];
                  const lastMsg = msgs[msgs.length - 1];
                  if (lastMsg?.role === 'assistant') {
                    msgs[msgs.length - 1] = {
                      ...lastMsg,
                      thinking: '',
                      statusLabel: undefined,
                    };
                  } else {
                    msgs.push({
                      id: `assistant-${Date.now()}`,
                      role: 'assistant',
                      content: '',
                      thinking: '',
                      toolResults: [],
                      timestamp: new Date(),
                      startedAt: Date.now(),
                    });
                  }
                  return { ...prev, messages: msgs };
                });
                const opened = startThinking(
                  thoughts,
                  Date.now(),
                  assistantContent.length,
                  toolResults.length
                );
                if (opened.length !== thoughts.length) {
                  thoughts = opened;
                  showThoughts();
                }
                currentEventType = '';
                continue;
              }

              if (
                currentEventType === 'thinking' &&
                parsed.content !== undefined
              ) {
                thinkingContent += parsed.content;
                thinkingDirty = true;
                scheduleFlush();
                // The label itself, for the wait row in the agent workspace.
                const label = String(parsed.content);
                if (label.trim()) progressed();
                setState((prev) => {
                  const msgs = [...prev.messages];
                  const lastMsg = msgs[msgs.length - 1];
                  if (lastMsg?.role !== 'assistant') return prev;
                  msgs[msgs.length - 1] = {
                    ...lastMsg,
                    statusLabel: withStatusLabel(lastMsg.statusLabel, label),
                  };
                  return { ...prev, messages: msgs };
                });
                currentEventType = '';
                continue;
              }

              if (currentEventType === 'thinking_end') {
                if (thoughts.some((spell) => spell.durationMs === undefined)) {
                  thoughts = endThinking(thoughts, Date.now());
                  showThoughts();
                }
                setState((prev) => {
                  const lastMsg = prev.messages.at(-1);
                  if (lastMsg?.role !== 'assistant' || !lastMsg.statusLabel)
                    return prev;
                  return {
                    ...prev,
                    messages: [
                      ...prev.messages.slice(0, -1),
                      { ...lastMsg, statusLabel: undefined },
                    ],
                  };
                });
                currentEventType = '';
                continue;
              }

              // The server took the turn, before any work of it.
              if (currentEventType === 'accepted') {
                msToAccepted ??= Date.now() - sentAt;
                markAccepted();
                currentEventType = '';
                continue;
              }

              // Clu writing a play: the composer field and the wait row read
              // its progress, and its agent's canvas draws it as it stands.
              if (currentEventType === 'play_draft') {
                const progress = drafts.draft(parsed);
                if (progress) {
                  progressed();
                  setState((prev) => {
                    const msgs = [...prev.messages];
                    const lastMsg = msgs[msgs.length - 1];
                    if (lastMsg?.role === 'assistant')
                      msgs[msgs.length - 1] = {
                        ...lastMsg,
                        statusLabel: progress.label,
                      };
                    return { ...prev, messages: msgs, progress };
                  });
                }
                currentEventType = '';
                continue;
              }

              // clu_model_v1 router: the model answering this turn, before
              // its reply (phase `turn`). Phase `next` is the model the next
              // turn steps up to: not this answer's.
              if (currentEventType === 'model') {
                const ownTurn = (parsed.phase ?? 'turn') === 'turn';
                const model = ownTurn ? cluAnsweredModelOf(parsed.model) : null;
                if (model && model !== turnModel) {
                  turnModel = model;
                  if (chatGenerationRef.current === generation)
                    setState((prev) => ({
                      ...prev,
                      messages: withAnswerModel(prev.messages, model),
                    }));
                }
                // The first one names the pick the server holds after
                // routing this turn.
                if (
                  ownTurn &&
                  routerOn &&
                  !pickReconciled &&
                  parsed.pick !== undefined
                ) {
                  pickReconciled = true;
                  reconcilePick(cluModelPickOf(parsed.pick));
                }
                currentEventType = '';
                continue;
              }

              // How full the prompt is: once at the start of the turn, and
              // again when a summary mid-turn shrinks it.
              if (currentEventType === 'context') {
                const contextMeter = parseContextMeter(parsed);
                if (contextMeter && chatGenerationRef.current === generation) {
                  setState((prev) => ({ ...prev, contextMeter }));
                }
                currentEventType = '';
                continue;
              }

              // A limit of the turn, after its recap: the chat goes on by
              // itself (or says the day's allowance is used), no error.
              if (currentEventType === 'turn_paused') {
                pausedTurn = turnPauseOf(parsed);
                currentEventType = '';
                continue;
              }

              if (
                currentEventType === 'text' &&
                parsed.content !== undefined &&
                !parsed.error
              ) {
                assistantContent += parsed.content;
                pendingText += parsed.content;
                scheduleFlush();
                msToFirstText ??= Date.now() - sentAt;
                progressed();
                currentEventType = '';
                continue;
              }

              // A tool Clu started: a live step row until its tool_result
              // (the same id) replaces it.
              if (currentEventType === 'tool_start') {
                progressed();
                const caption = captionOf(parsed.caption);
                const running: RunningTool = {
                  id: String(parsed.id ?? `tool-${Date.now()}`),
                  name: String(parsed.name ?? ''),
                  textOffset: assistantContent.length,
                  ...(caption ? { caption } : {}),
                  startedAt: Date.now(),
                };
                startedCalls = [...startedCalls, running];
                setState((prev) => {
                  const msgs = [...prev.messages];
                  const lastMsg = msgs[msgs.length - 1];
                  if (lastMsg?.role === 'assistant') {
                    msgs[msgs.length - 1] = {
                      ...lastMsg,
                      runningTools: [...(lastMsg.runningTools ?? []), running],
                    };
                  } else {
                    msgs.push({
                      id: `assistant-${Date.now()}`,
                      role: 'assistant',
                      content: '',
                      toolResults: [],
                      runningTools: [running],
                      timestamp: new Date(),
                      startedAt: Date.now(),
                    });
                  }
                  return { ...prev, messages: msgs };
                });
                currentEventType = '';
                continue;
              }

              const isTypedToolResult = currentEventType === 'tool_result';
              const isLegacyToolResult =
                parsed.name &&
                parsed.result !== undefined &&
                parsed.display !== undefined;
              if (isTypedToolResult || isLegacyToolResult) {
                progressed();
                const answered = {
                  id: String(parsed.id || `tool-${Date.now()}`),
                  name: String(parsed.name ?? ''),
                };
                if (
                  confirmedAction &&
                  (runStarted || answered.id === `tool-${confirmedAction}`)
                )
                  actionRan = true;
                // The call this answers: its caption when the result has
                // none, and how long it ran.
                const started = runningToolFor(startedCalls, answered);
                startedCalls = withoutRunning(startedCalls, answered) ?? [];
                const caption = captionOf(parsed.caption) ?? started?.caption;
                const nextToolResult = {
                  id: answered.id,
                  name: parsed.name,
                  result: parsed.result,
                  display: parsed.display || {},
                  ...(parsed.input_args
                    ? { input_args: parsed.input_args }
                    : {}),
                  textOffset: assistantContent.length,
                  ...(caption ? { caption } : {}),
                  // The tool's own run time from the server when it sends one:
                  // parallel tools' results arrive together, so timing their
                  // events here gives each the whole batch's time.
                  ...(typeof parsed.duration_ms === 'number' &&
                  parsed.duration_ms >= 0
                    ? { durationMs: parsed.duration_ms }
                    : started?.startedAt !== undefined
                      ? {
                          durationMs: Math.max(
                            0,
                            Date.now() - started.startedAt
                          ),
                        }
                      : {}),
                } satisfies ToolResultData;
                toolResults.push(nextToolResult);
                if (nextToolResult.display?.type === 'question_card')
                  sawActionableCard = true;
                // The proposal the drawn play became, or the error that ends
                // it: the composer field stops saying it is being written.
                const draftEnded = drafts.result(nextToolResult);

                // Dispatch agent commands to the UI
                const command: AgentFilterCommand | undefined =
                  nextToolResult.display?.type === 'apply_filters'
                    ? agentFilterCommandOf(nextToolResult.result)
                    : undefined;
                if (command) {
                  // A background session must not re-filter the page the user
                  // is looking at; hold it until they switch to this session.
                  if (isActiveRef.current) {
                    dispatch(setAgentApplyFilters(command));
                  } else {
                    heldFilterCommandRef.current = command;
                  }
                }

                // Cache refresh after a Copilot mutation is now driven SOLELY by
                // the canonical `lead_list_update` WebSocket event the BE
                // per-operation service emits (handled in
                // use-unified-websocket.hook.ts, keyed off affected_list_ids).
                // The SSE handler no longer drives refresh — it only appends the
                // tool result for rendering. display.type is presentation-only.

                setState((prev) => {
                  const msgs = [...prev.messages];
                  const lastMsg = msgs[msgs.length - 1];
                  if (lastMsg?.role === 'assistant') {
                    msgs[msgs.length - 1] = {
                      ...lastMsg,
                      toolResults: [
                        ...(lastMsg.toolResults || []),
                        nextToolResult,
                      ],
                      runningTools: withoutRunning(
                        lastMsg.runningTools,
                        nextToolResult
                      ),
                      // A play draft's label ends with its tool (no
                      // thinking_end follows).
                      statusLabel: undefined,
                    };
                  } else {
                    msgs.push({
                      id: `assistant-${Date.now()}`,
                      role: 'assistant',
                      content: '',
                      toolResults: [nextToolResult],
                      timestamp: new Date(),
                      startedAt: Date.now(),
                    });
                  }
                  return {
                    ...prev,
                    messages: msgs,
                    ...(draftEnded ? { progress: null } : {}),
                  };
                });
                currentEventType = '';
                continue;
              }

              const isTypedConfirmation =
                currentEventType === 'confirmation_request';
              const isLegacyConfirmation =
                parsed.tool_name && parsed.description && parsed.id;
              if (isTypedConfirmation || isLegacyConfirmation) {
                progressed();
                sawActionableCard = true;
                confirmationRequest = parsed as ConfirmationRequest;
                awaitingConfirmation = true;
                setState((prev) => {
                  const msgs = [...prev.messages];
                  const lastMsg = msgs[msgs.length - 1];
                  if (lastMsg?.role === 'assistant') {
                    msgs[msgs.length - 1] = { ...lastMsg, confirmationRequest };
                  } else {
                    msgs.push({
                      id: `assistant-${Date.now()}`,
                      role: 'assistant',
                      content: '',
                      confirmationRequest,
                      timestamp: new Date(),
                      startedAt: Date.now(),
                    });
                  }
                  return { ...prev, messages: msgs };
                });
                currentEventType = '';
                continue;
              }

              const isDoneEvent = currentEventType === 'done';
              // `done` finishes the turn: stop reading, so a transport error
              // before the trailing [DONE] can't fail a completed reply.
              if (isDoneEvent) terminalCompleted = streamDone = true;
              if (isDoneEvent || parsed.conversation_id !== undefined) {
                conversationId = parsed.conversation_id;
                // Known now, not when the turn ends: the chat is real from
                // the first event that names it (run_started, else done).
                if (
                  typeof parsed.conversation_id === 'string' &&
                  parsed.conversation_id
                )
                  nameChat(parsed.conversation_id);
              }
              if (
                (isDoneEvent || parsed.awaiting_confirmation) &&
                parsed.awaiting_confirmation
              ) {
                awaitingConfirmation = true;
              }
              if (isDoneEvent || currentEventType === 'run_started') {
                if (!isDoneEvent) {
                  runStarted = true;
                  markAccepted();
                }
                currentEventType = '';
                continue;
              }

              // Errors
              if (currentEventType === 'error' || parsed.error) {
                currentEventType = '';
                throw new AgentChatRequestError(
                  parsed.error || parsed.message || 'Chat failed',
                  typeof parsed.code === 'string' ? parsed.code : undefined,
                  undefined,
                  undefined,
                  typeof parsed.retryable === 'boolean'
                    ? parsed.retryable
                    : undefined
                );
              }

              // Backward compatible fallback for old untyped text chunks
              if (parsed.content !== undefined && !parsed.error) {
                assistantContent += parsed.content;
                pendingText += parsed.content;
                scheduleFlush();
              }
            } catch (parseError) {
              if (parseError instanceof SyntaxError) continue;
              throw parseError;
            }
          }
        }

        if (!terminalCompleted) {
          throw new Error(CONNECTION_ENDED_MESSAGE);
        }
        flushAndTeardown();

        if (chatGenerationRef.current !== generation) return stopped();
        // A turn that ended without naming a chat left none to show.
        if (startsChat && !namedId)
          setLocalChats((prev) => dropChat(prev, chatKey));
        setState((prev) => {
          const msgs = [...prev.messages];
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg?.role === 'assistant') {
            const finalContent = assistantContent || lastMsg.content;
            const finalThinking = thinkingContent || lastMsg.thinking;
            const finalTools =
              toolResults.length > 0 ? toolResults : lastMsg.toolResults;
            const finalConfirmation =
              confirmationRequest || lastMsg.confirmationRequest;

            // Remove empty placeholder if stream produced nothing
            if (
              !finalContent &&
              !finalThinking &&
              !finalTools?.length &&
              !finalConfirmation
            ) {
              msgs.pop();
            } else {
              msgs[msgs.length - 1] = {
                ...lastMsg,
                content: finalContent,
                ...(finalThinking ? { thinking: finalThinking } : {}),
                toolResults: finalTools,
                ...(thoughts.length ? { thoughts } : {}),
                confirmationRequest: finalConfirmation,
                completedAt: Date.now(),
                ...(turnModel ? { model: turnModel } : {}),
                ...(pausedTurn?.resetsAt
                  ? { resetsAt: pausedTurn.resetsAt }
                  : {}),
              };
            }
          } else if (
            assistantContent ||
            toolResults.length > 0 ||
            confirmationRequest
          ) {
            msgs.push({
              id: `assistant-${Date.now()}`,
              role: 'assistant',
              content: assistantContent,
              ...(thinkingContent ? { thinking: thinkingContent } : {}),
              toolResults,
              ...(thoughts.length ? { thoughts } : {}),
              confirmationRequest,
              timestamp: new Date(),
              completedAt: Date.now(),
              ...(turnModel ? { model: turnModel } : {}),
              ...(pausedTurn?.resetsAt
                ? { resetsAt: pausedTurn.resetsAt }
                : {}),
            });
          }
          return {
            ...prev,
            messages: msgs,
            isStreaming: false,
            conversationId: conversationId || prev.conversationId,
            awaitingConfirmation,
            pendingConfirmation: confirmationRequest || null,
          };
        });
        // A play drawn as it was written: sent once its proposal shows on
        // the canvas (it loads after the turn's result), or without it.
        const handedOver = drafts.end();
        if (handedOver)
          void whenProposalRendered(handedOver, PROPOSAL_RENDER_WAIT_MS).then(
            capture
          );
        else capture(null);
        endTurn(
          awaitingConfirmation ? 'awaiting_confirmation' : 'done',
          conversationId || namedId || requestConversationId,
          awaitingConfirmation ? null : pausedTurn
        );

        return { ok: true };
      } catch (caught) {
        let error = caught;
        // The stream dropped (or a retry found the turn still running) in a
        // chat the server knows: it may hold the turn all the same, so the
        // chat follows it there rather than failing on "network error".
        const followId = namedId ?? requestConversationId;
        if (
          followId &&
          chatGenerationRef.current === generation &&
          (!retry || ownsRetry()) &&
          !acceptedReview &&
          !owned &&
          !options?.confirmedAction &&
          !options?.cancelledAction &&
          (ordinaryText || Boolean(options?.userInput)) &&
          !terminalCompleted &&
          !sawActionableCard &&
          mayStillRun(error)
        ) {
          flushAndTeardown();
          if (
            await followTurnOnServer(
              followId,
              clientTurnId,
              generation,
              controller.signal,
              FOLLOW_READS,
              (outcome) => {
                if (outcome !== 'done' && outcome !== 'awaiting_confirmation')
                  return;
                const handedOver = drafts.end();
                if (handedOver)
                  void whenProposalRendered(
                    handedOver,
                    PROPOSAL_RENDER_WAIT_MS
                  ).then(capture);
                else capture(null);
              },
              drafts
            )
          )
            return { ok: false, aborted: true };
          // Stop while the chat was read: it ends as a stop, not a failure.
          if (controller.signal.aborted)
            error = new DOMException('Stopped', 'AbortError');
        }
        if (
          confirmedAction &&
          !actionRan &&
          !approvalClaimed &&
          chatGenerationRef.current === generation &&
          mayStillRun(error)
        ) {
          // The server can claim the token without its status reaching us.
          // Close the card only when a read of the saved chat shows this
          // approval spent or replaced. An unreadable chat (offline, a
          // deploy's 502s) leaves it live: a dropped connection is when the
          // confirm most likely never arrived, and the server's single-use
          // token still refuses a second run.
          if (followId) {
            try {
              const token = await fetchWebtokenHeadless();
              const res = await fetch(
                `${getApiBase()}/api/v1/agent/conversations/${followId}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  signal: controller.signal,
                }
              );
              if (res.ok) {
                const data: SavedChatRead = await res.json();
                const pending = restoredConfirmationOf(
                  (data.messages ?? []).map(storedMessageToAgentMessage)
                );
                const actionToken = options?.confirmedArgs?.action_token;
                approvalClaimed = !(
                  data.turn_running === false &&
                  typeof actionToken === 'string' &&
                  !!actionToken &&
                  pending?.tool_name === confirmedAction &&
                  pending.action_token === actionToken &&
                  (parseServerDate(pending.expires_at)?.getTime() ?? 0) >
                    Date.now()
                );
              }
            } catch {
              // Unreadable: the card stays live (see above).
            }
          }
          if (controller.signal.aborted)
            error = new DOMException('Stopped', 'AbortError');
        }
        if (chatGenerationRef.current !== generation) {
          flushAndTeardown();
          return stopped();
        }
        if ((error as Error).name === 'AbortError' || (retry && !ownsRetry())) {
          flushAndTeardown();
          if (startsChat) setLocalChats((prev) => orphanChat(prev, chatKey));
          setState((prev) => {
            const msgs = [...prev.messages];
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg?.role === 'assistant') {
              // If placeholder is still empty, remove it; otherwise keep partial content
              if (
                !displayedContent &&
                !lastMsg.content &&
                !thinkingContent &&
                !lastMsg.thinking &&
                !lastMsg.toolResults?.length
              ) {
                msgs.pop();
              } else {
                msgs[msgs.length - 1] = {
                  ...lastMsg,
                  content: displayedContent || lastMsg.content,
                  ...(thinkingContent ? { thinking: thinkingContent } : {}),
                  completedAt: Date.now(),
                };
              }
            }
            return { ...prev, messages: msgs, isStreaming: false };
          });
          // The same chat, so the user pressed Stop.
          endTurn('stopped');
          return { ok: false, aborted: true, ...ran() };
        }
        if (
          ordinaryText &&
          canRecoverTextTurn(error) &&
          !terminalCompleted &&
          !sawActionableCard &&
          !hasPendingRetryCard(stateRef.current.messages) &&
          sendScope.active &&
          sendScope.organizationId &&
          user?.id &&
          retryScopeRef.current.scope === sendScope.scope &&
          retryScopeRef.current.epoch === sendScope.epoch &&
          !stateRef.current.awaitingConfirmation &&
          !stateRef.current.pendingConfirmation
        ) {
          const receipt: FailedTextTurn = {
            id: crypto.randomUUID(),
            messageId: userMessageId,
            message,
            ...(image ? { image } : {}),
            clientTurnId,
            conversationId: namedId ?? requestConversationId,
            scope: sendScope.scope,
            epoch: sendScope.epoch,
            generation,
            organizationId: sendScope.organizationId,
            context: { ...(retry?.context ?? sendContext) },
          };
          failedTextRef.current = receipt;
          setFailedTextTurn(receipt);
        }
        const errorMsg = error instanceof Error ? error.message : 'Chat failed';
        const errorCode =
          error instanceof AgentChatRequestError ? error.code : undefined;
        // The turn is over: its text shows whole, a tool that never answered
        // stops running and an open thought ends (a turn a limit stopped
        // still shows what it did, and its Continue).
        flushAndTeardown();
        const shownText = displayedContent;
        const paymentNotice =
          error instanceof AgentChatRequestError
            ? (error.paidAccess ??
              paidAccessNotice({
                data: { detail: { code: error.code, message: error.message } },
              }))
            : null;
        if (paymentNotice) requestPaidAccess(paymentNotice);
        // Too few credits to start a turn: the credits dialog (plans and
        // one-time packs), as every action that runs out opens it.
        // Credits held by Clu chats still running (credits_held) come back
        // as those chats finish: the chat says so and opens no dialog.
        if (errorCode === 'insufficient_credits')
          dispatch(openCreditGate({ action: 'Clu' }));
        setFailure(errorCode ? { message: errorMsg, code: errorCode } : null);
        // Failed before the chat had an id: its row goes. After the id, the
        // chat is real and the next read settles it.
        if (startsChat) setLocalChats((prev) => dropChat(prev, chatKey));
        setState((prev) => {
          const msgs = [...prev.messages];
          const lastMsg = msgs[msgs.length - 1];
          // Remove empty assistant placeholder if no content was received
          if (
            lastMsg?.role === 'assistant' &&
            !shownText &&
            !lastMsg.content &&
            !lastMsg.thinking &&
            !lastMsg.toolResults?.length
          ) {
            msgs.pop();
          } else if (lastMsg?.role === 'assistant') {
            msgs[msgs.length - 1] = {
              ...lastMsg,
              content: shownText || lastMsg.content,
              completedAt: lastMsg.completedAt ?? Date.now(),
              endedInError: true,
            };
          }
          // A review answer the server refused (4xx, so it never ran): drop
          // it, so its card reads open again rather than answered. Other
          // failures keep it for Retry, which re-sends the same turn.
          if (
            options?.userInput &&
            !options.skipUserMessage &&
            error instanceof AgentChatRequestError &&
            error.httpStatus !== undefined &&
            error.httpStatus < 500 &&
            msgs.at(-1)?.id === userMessageId
          )
            msgs.pop();
          return {
            ...prev,
            messages: msgs,
            isStreaming: false,
            error: errorMsg,
          };
        });
        endTurn('failed');

        return {
          ok: false,
          ...(errorCode ? { errorCode } : {}),
          ...(acceptedReview
            ? {
                retryable:
                  error instanceof AgentChatRequestError
                    ? error.retryable === true
                    : true,
              }
            : {}),
          ...ran(),
        };
      } finally {
        if (reader && !readerFinished)
          void reader.cancel().catch(() => undefined);
        if (abortControllerRef.current === controller)
          abortControllerRef.current = null;
        if (retryControllerRef.current?.controller === controller)
          retryControllerRef.current = null;
        owned?.signal.removeEventListener('abort', abortOwned);
        if (retryAttemptRef.current === retry) retryAttemptRef.current = null;
        // The turn ended, failed or was left: a draft never handed over goes.
        // A turn followed on the server keeps it, and the follow ends it.
        if (watchedDraftsRef.current !== drafts) drafts.end();
      }
    },
    [
      posthog,
      user,
      dispatch,
      forgetTextRetry,
      savePick,
      movePick,
      orgId,
      userId,
      historyOrigin,
      failedTextTurn,
      scope,
      followTurnOnServer,
    ]
  );

  const reviewAcceptedPlay = useCallback(
    (request: AcceptedPlayReviewRequest) =>
      sendMessage('', {
        acceptedPlayReview: request,
        expectedConversationId: request.conversationId,
        skipUserMessage: true,
      }),
    [sendMessage]
  );

  /**
   * A paused turn goes on by itself: sends Continue into the same chat, up to
   * `MAX_AUTO_CONTINUES` times for one user message, then leaves it to the
   * Continue button the server offered. Null when it sent nothing (the turn did
   * not pause for a limit a new turn clears, it was the read-only review of an
   * accepted play, the chat moved on, or the count is used up). A user message
   * sent since starts a new count. Whether the surface may continue by itself
   * at all (the composer, queued sends) is for the caller.
   */
  const continuePausedTurn = useCallback(
    async (turn: LastTurn): Promise<SendResult | null> => {
      if (turn.outcome !== 'done' || !turn.pause?.autoContinue) return null;
      // A review runs on a read-only toolkit: a plain Continue would start a
      // chat turn with every tool.
      if (turn.review) return null;
      const current = stateRef.current;
      if (
        !turn.conversationId ||
        current.conversationId !== turn.conversationId ||
        current.isStreaming ||
        current.awaitingConfirmation ||
        current.pendingConfirmation ||
        loadingConversationRef.current
      )
        return null;
      const key = `${turn.conversationId}:${continuedMessageRef.current}`;
      const used = autoContinuesRef.current.get(key) ?? 0;
      if (used >= MAX_AUTO_CONTINUES) return null;
      autoContinuesRef.current.set(key, used + 1);
      return sendMessage(CONTINUE_MESSAGE, { autoContinue: true });
    },
    [sendMessage]
  );

  const retryMessage = useCallback(
    async (message: AgentMessage): Promise<SendResult> => {
      let clientTurnId = message.clientTurnId;
      if (!clientTurnId) {
        clientTurnId = retryTurnIdsRef.current.get(message.id);
      }
      if (!clientTurnId) {
        clientTurnId = crypto.randomUUID();
        retryTurnIdsRef.current.set(message.id, clientTurnId);
      }

      return sendMessage(message.content, {
        clientTurnId,
        skipUserMessage: true,
        historyBeforeMessageId: message.id,
        userInput: message.userInput,
        ...(message.autoContinue ? { autoContinue: true } : {}),
        ...(message.userInput && stateRef.current.conversationId
          ? { expectedConversationId: stateRef.current.conversationId }
          : {}),
      });
    },
    [sendMessage]
  );

  const retryFailedTextTurn = useCallback(
    async (id: string, onClaim?: () => void): Promise<SendResult> => {
      const receipt = failedTextRef.current;
      const current = stateRef.current;
      const lastUser = [...current.messages]
        .reverse()
        .find((item) => item.role === 'user');
      if (
        !receipt ||
        receipt.id !== id ||
        !retryScopeRef.current.active ||
        retryScopeRef.current.scope !== receipt.scope ||
        retryScopeRef.current.epoch !== receipt.epoch ||
        chatGenerationRef.current !== receipt.generation ||
        current.conversationId !== receipt.conversationId ||
        current.isStreaming ||
        loadingConversationRef.current ||
        current.awaitingConfirmation ||
        current.pendingConfirmation ||
        hasPendingRetryCard(current.messages) ||
        lastUser?.id !== receipt.messageId ||
        lastUser.content !== savedUserText(receipt.message, receipt.image) ||
        lastUser.clientTurnId !== receipt.clientTurnId ||
        lastUser.userInput
      )
        return { ok: false, aborted: true };
      // Claim synchronously. Neither a second click nor a stale handler can replay it.
      failedTextRef.current = null;
      setFailedTextTurn(null);
      retryAttemptRef.current = receipt;
      // Runtime queue bookkeeping belongs only to an accepted claim. A stale
      // button must not hold a different chat's queue while refusing the send.
      onClaim?.();
      return sendMessage(
        receipt.message,
        {
          skipUserMessage: true,
          clientTurnId: receipt.clientTurnId,
          historyBeforeMessageId: receipt.messageId,
          ...(lastUser.autoContinue ? { autoContinue: true } : {}),
        },
        receipt
      );
    },
    [sendMessage]
  );

  const confirmAction = useCallback(
    async (
      selection?: {
        enrichment_type?: string;
        count?: number;
        list_id?: string;
        list_name?: string;
        subject?: string;
        body?: string;
        _disambiguation?: boolean;
      },
      expected?: TryConfirmationIdentity | ConfirmationIdentity
    ): Promise<SendResult | undefined> => {
      const pending = stateRef.current.pendingConfirmation;
      if (!pending) return undefined;
      const tried = !!expected && 'quoteId' in expected;
      // Try IDs repeat across cards. Only the displayed token and quote can
      // authorize the pending paid action, including through a stale handler.
      if (pending.tool_name === 'run_agent_sample' || tried) {
        if (
          !tried ||
          pending.tool_name !== 'run_agent_sample' ||
          !expected.actionToken ||
          !expected.quoteId ||
          expected.actionToken !== pending.action_token ||
          expected.quoteId !== pending.arguments.quote_id
        )
          return undefined;
      } else if (
        expected &&
        (expected.action !== pending.tool_name ||
          expected.actionToken !== pending.action_token)
      ) {
        // Send cards of one kind share an id: a click from the card shown
        // before this one confirms nothing.
        return undefined;
      }

      posthog?.capture('agent_chat_action_confirmed', {
        tool_name: pending.tool_name,
        context_type: contextRef.current.type,
        lead_list_id: contextRef.current.lead_list_id,
        conversation_id: stateRef.current.conversationId,
        credits_required: pending.credits_required,
        user_id: user?.id,
      });

      let clientTurnId =
        pending.clientTurnId ?? confirmationTurnIdsRef.current.get(pending.id);
      if (!clientTurnId) {
        clientTurnId = crypto.randomUUID();
      }
      confirmationTurnIdsRef.current.set(pending.id, clientTurnId);
      const withTurn =
        pending.clientTurnId === clientTurnId
          ? pending
          : { ...pending, clientTurnId };
      // The email card's subject and email the user edited come back with the
      // card on a retry, so a second Confirm sends their words, not the draft.
      const pendingForRetry =
        pending.tool_name === 'send_mailbox_email' &&
        typeof selection?.subject === 'string' &&
        typeof selection?.body === 'string'
          ? {
              ...withTurn,
              arguments: {
                ...withTurn.arguments,
                subject: selection.subject,
                body: selection.body,
              },
            }
          : withTurn;

      const confirmMsg: AgentMessage = {
        id: `user-confirm-${clientTurnId}`,
        role: 'user',
        content: 'Confirmed',
        clientTurnId,
        timestamp: new Date(),
      };
      // Clear pendingConfirmation on click so a double-click can't fire a
      // second confirm (Billable-op SSOT, belt-and-suspenders — the
      // server's single-use action_token is the real guard). A
      // disambiguation re-issues a fresh confirmation, so keep the card
      // up for that flow.
      setState((prev) => ({
        ...prev,
        messages: prev.messages.some((message) => message.id === confirmMsg.id)
          ? prev.messages
          : [...prev.messages, confirmMsg],
        pendingConfirmation: selection?._disambiguation
          ? pendingForRetry
          : null,
      }));

      // Merge selection overrides (e.g. enrichment_type, count) into confirmed args
      const confirmedArgs: Record<string, unknown> = selection
        ? { ...pending.arguments, ...selection }
        : { ...pending.arguments };

      // Forward expiry so the backend can validate it
      if (pending.expires_at) {
        confirmedArgs.expires_at = pending.expires_at;
      }

      // Forward the server-minted single-use action_token so the backend
      // claims this action exactly once. A replayed confirm carries the
      // same token and is rejected at claim (no double import / charge).
      if (pending.action_token) {
        confirmedArgs.action_token = pending.action_token;
      }

      const sending = sendMessage(lastUserMessageRef.current, {
        confirmedAction: pending.tool_name,
        confirmedArgs,
        skipUserMessage: true,
        clientTurnId,
      });
      const generation = chatGenerationRef.current;
      const result = await sending;
      if (chatGenerationRef.current !== generation) return result;

      const approvalClosed = result?.approvalClosed === true;
      const approvalRestartRequired =
        result?.errorCode === APPROVAL_RESTART_REQUIRED || approvalClosed;
      if (
        result?.ok ||
        result?.aborted ||
        result?.actionRan ||
        approvalRestartRequired
      ) {
        confirmationTurnIdsRef.current.delete(pending.id);
      }

      // Keep the transcript factual: Confirmed stays once the action ran,
      // even when Clu's step after it stopped on a limit, failed or was
      // stopped, and the card stays spent (the send cleared it), as after a
      // clean confirm. Ordinary transport/SSE failures before the action ran
      // restore the original approval and its stable retry identity. A
      // server-authoritative restart error retires the stale token instead of
      // offering an invalid retry forever, and so does a turn that failed or
      // dropped after the server claimed the approval (the action may have
      // run, so Confirmed stays). A retired card shows as expired, not as
      // live or completed.
      if (result && !result.ok && !result.actionRan) {
        setState((prev) => ({
          ...prev,
          messages: (approvalClosed
            ? prev.messages
            : prev.messages.filter((message) => message.id !== confirmMsg.id)
          ).map((message) =>
            approvalRestartRequired &&
            pending.action_token &&
            message.confirmationRequest?.action_token === pending.action_token
              ? {
                  ...message,
                  confirmationRequest: {
                    ...message.confirmationRequest,
                    // Already past, so the resolved card reads expired.
                    expires_at: new Date(Date.now() - 1000).toISOString(),
                  },
                }
              : message
          ),
          // Stop leaves the card as the stopped turn left it.
          ...(result.aborted
            ? {}
            : {
                awaitingConfirmation: !approvalRestartRequired,
                pendingConfirmation: approvalRestartRequired
                  ? null
                  : prev.pendingConfirmation || pendingForRetry,
              }),
        }));
      }

      // Surface the result so the confirmation UI (copilot-tool-uis) can
      // re-enable its Confirm button on a failed (non-aborted) execute.
      return result;
    },
    [sendMessage, posthog, user]
  );

  const rejectAction = useCallback(
    async (reasonInput?: unknown): Promise<SendResult | undefined> => {
      // The user's words on the card (a click handler passes its event).
      const reason =
        typeof reasonInput === 'string' && reasonInput.trim()
          ? reasonInput.trim()
          : undefined;
      const pending = stateRef.current.pendingConfirmation;
      if (!pending) return undefined;

      // Reject is a server-authorized continuation of the pending approval.
      // Never send the browser-held action arguments or a client-selected root.
      // The opaque token plus the persisted conversation let the backend bind
      // the exact approval, actor, waiting receipt, and billing tree.
      if (!pending.action_token || !stateRef.current.conversationId) {
        cancellationTurnIdsRef.current.delete(pending.id);
        setState((prev) => ({
          ...prev,
          awaitingConfirmation: false,
          pendingConfirmation: null,
          error:
            'This approval can no longer be cancelled. Ask Clu to restart it.',
        }));
        return { ok: false };
      }

      let clientTurnId =
        pending.cancelClientTurnId ??
        cancellationTurnIdsRef.current.get(pending.id);
      if (!clientTurnId) {
        clientTurnId = crypto.randomUUID();
      }
      cancellationTurnIdsRef.current.set(pending.id, clientTurnId);
      const pendingForRetry =
        pending.cancelClientTurnId === clientTurnId
          ? pending
          : { ...pending, cancelClientTurnId: clientTurnId };

      posthog?.capture('agent_chat_action_rejected', {
        tool_name: pending.tool_name,
        context_type: contextRef.current.type,
        lead_list_id: contextRef.current.lead_list_id,
        conversation_id: stateRef.current.conversationId,
        user_id: user?.id,
      });

      const cancelMessageId = `user-cancel-${clientTurnId}`;
      // Add the visible cancellation once while the request is active. It stays
      // in the transcript only after the server durably accepts the cancellation.
      setState((prev) => ({
        ...prev,
        messages: prev.messages.some(
          (message) => message.id === cancelMessageId
        )
          ? prev.messages
          : [
              ...prev.messages,
              {
                id: cancelMessageId,
                role: 'user',
                content: reason ?? 'Cancelled',
                clientTurnId,
                timestamp: new Date(),
              },
            ],
        awaitingConfirmation: false,
        pendingConfirmation: null,
      }));

      const sending = sendMessage('Cancelled', {
        cancelledAction: pending.tool_name,
        cancelledArgs: {
          action_token: pending.action_token,
          ...(reason ? { reason } : {}),
        },
        skipUserMessage: true,
        clientTurnId,
      });
      const generation = chatGenerationRef.current;
      const result = await sending;
      if (chatGenerationRef.current !== generation) return result;

      const approvalRestartRequired =
        result.errorCode === APPROVAL_RESTART_REQUIRED;
      if (result.ok || result.aborted || approvalRestartRequired) {
        cancellationTurnIdsRef.current.delete(pending.id);
      }

      // A transport/SSE failure did not durably cancel the approval. Remove the
      // optimistic transcript entry and restore the identical token and turn
      // identity. A restart-required response instead retires stale authority.
      if (!result.ok && !result.aborted) {
        setState((prev) => ({
          ...prev,
          messages: prev.messages.filter(
            (message) => message.id !== cancelMessageId
          ),
          awaitingConfirmation: !approvalRestartRequired,
          pendingConfirmation: approvalRestartRequired
            ? null
            : prev.pendingConfirmation || pendingForRetry,
        }));
      } else if (result.aborted) {
        setState((prev) => ({
          ...prev,
          messages: prev.messages.filter(
            (message) => message.id !== cancelMessageId
          ),
        }));
      }

      return result;
    },
    [sendMessage, posthog, user]
  );

  const cancelStream = useCallback(() => {
    forgetTextRetry();
    // Closing the stream no longer ends the turn on the server: Stop does.
    void stopServerTurn(stateRef.current.conversationId, liveTurnIdRef.current);
    holdTabTurn(null);
    abortControllerRef.current?.abort();
    // A followed turn's draft never handed over goes, and so does its
    // progress.
    watchedDraftsRef.current?.end();
    setLocalChats((prev) => orphanChat(prev));
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      progress: null,
      messages: prev.isStreaming
        ? withStoppedNote(prev.messages)
        : prev.messages,
    }));
  }, [forgetTextRetry, holdTabTurn]);

  const clearChat = useCallback(() => {
    if (!historyMounted.current) return;
    forgetTextRetry();
    // Before a closed session's host unmounts with it in the same update.
    holdTabTurn(null);
    abortControllerRef.current?.abort();
    setLocalChats((prev) => orphanChat(prev));
    chatGenerationRef.current += 1;
    retryTurnIdsRef.current.clear();
    confirmationTurnIdsRef.current.clear();
    cancellationTurnIdsRef.current.clear();
    lastUserMessageRef.current = '';
    // A conversation still opening is superseded: its read is dropped.
    loadingConversationRef.current = false;
    setIsLoadingConversation(false);
    heldFilterCommandRef.current = null;
    const next: AgentChatState = {
      messages: [],
      isStreaming: false,
      error: null,
      conversationId: null,
      awaitingConfirmation: false,
      pendingConfirmation: null,
      contextMeter: null,
      progress: null,
      // A new chat starts on the member's default (the starred row).
      modelPick: cluNewChatPick(),
    };
    newChatPicksRef.current = pickChangesRef.current;
    notePickChosen();
    // A keyed workspace may snapshot in the same cleanup/mount batch.
    stateRef.current = next;
    setState(next);
    if (sessionKey) {
      dispatch(setAiChatSessionState({ key: sessionKey }));
      return;
    }
    dispatch(setAiChatState(undefined));
    dispatch(setAiChatConversationId(undefined));
  }, [dispatch, sessionKey, forgetTextRetry, holdTabTurn, notePickChosen]);

  /**
   * clu_model_v1 router: the user's model for this chat, or Auto. A named
   * chat saves it at once and takes it back when the server refuses it; a
   * new chat holds it for its first turn, which carries it, even when it is
   * the pick the picker already showed. `null` (a seed that chose none): a
   * new chat goes back to the member's default and its first turn sends
   * none. False when the server refused it.
   */
  const setModelPick = useCallback(
    async (choice: CluModelPick | null): Promise<boolean> => {
      if (!historyMounted.current || historyEpoch.current !== historyOrigin)
        return false;
      const { conversationId: id, modelPick: previous } = stateRef.current;
      if (choice === null && !id) {
        newChatPicksRef.current = pickChangesRef.current;
        notePickChosen();
      }
      const pick = choice ?? cluNewChatPick();
      if (choice !== null && !id) {
        pickChangesRef.current += 1;
        notePickChosen();
      }
      if (previous === pick) return true;
      if (id) pickChangesRef.current += 1;
      const picked = { ...stateRef.current, modelPick: pick };
      stateRef.current = picked;
      setState((prev) => ({ ...prev, modelPick: pick }));
      if (!id) return true;
      return savePick(id, pick, previous);
    },
    [savePick, historyOrigin, notePickChosen]
  );

  /** The chat as it stands now. */
  const snapshotChat = useCallback(
    (): ChatSnapshot => ({
      messages: stateRef.current.messages,
      conversationId: stateRef.current.conversationId,
      awaitingConfirmation: stateRef.current.awaitingConfirmation,
      pendingConfirmation: stateRef.current.pendingConfirmation,
      contextMeter: stateRef.current.contextMeter,
      modelPick: stateRef.current.modelPick,
    }),
    []
  );

  /** Puts a chat set aside back as it was; a running stream is left behind. */
  const replaceChat = useCallback(
    (snapshot: ChatSnapshot) => {
      if (!historyMounted.current) return;
      forgetTextRetry();
      abortControllerRef.current?.abort();
      setLocalChats((prev) => orphanChat(prev));
      chatGenerationRef.current += 1;
      retryTurnIdsRef.current.clear();
      confirmationTurnIdsRef.current.clear();
      cancellationTurnIdsRef.current.clear();
      const lastUser = [...snapshot.messages]
        .reverse()
        .find((message) => message.role === 'user');
      lastUserMessageRef.current = lastUser?.content ?? '';
      // An id put back is loaded already: the auto-load must not read it again.
      autoLoadedConvRef.current = snapshot.conversationId;
      loadingConversationRef.current = false;
      setIsLoadingConversation(false);
      const next: AgentChatState = {
        ...snapshot,
        contextMeter: snapshot.contextMeter ?? null,
        progress: null,
        modelPick: snapshot.modelPick ?? 'auto',
        isStreaming: false,
        error: null,
      };
      // Publish the handoff before another workspace takes its own snapshot.
      stateRef.current = next;
      setState(next);
      // Keep the auto-load fallback from reopening the chat just left before
      // the ordinary persistence effect runs, including when restoring blank.
      const serialized: SerializedChatState = {
        messages: serializeMessages(next.messages),
        conversationId: next.conversationId,
        awaitingConfirmation: next.awaitingConfirmation,
        pendingConfirmation: next.pendingConfirmation,
        contextMeter: next.contextMeter,
        modelPick: next.modelPick,
        lastUserMessage: lastUserMessageRef.current || undefined,
      };
      if (sessionKey) {
        dispatch(
          setAiChatSessionState({ key: sessionKey, chatState: serialized })
        );
      } else {
        dispatch(setAiChatState(serialized));
        dispatch(setAiChatConversationId(next.conversationId ?? undefined));
      }
    },
    [dispatch, sessionKey, forgetTextRetry]
  );

  return {
    ...state,
    errorCode: failure && state.error === failure.message ? failure.code : null,
    // A view switch revokes Retry authority, but does not resolve that user turn.
    hasPendingTextRetry: failedTextTurn?.scope === scope,
    failedTextTurn: (failedTextRef.current === failedTextTurn &&
    failedTextTurn?.scope === scope &&
    isActive &&
    !state.isStreaming &&
    !isLoadingConversation &&
    !state.awaitingConfirmation &&
    !state.pendingConfirmation &&
    !hasPendingRetryCard(state.messages)
      ? failedTextTurn
      : null) as FailedTextTurnRetry | null,
    retryFailedTextTurn,
    lastTurn,
    conversations,
    localChats,
    settleAgentChats,
    isLoadingHistory,
    historyFailed,
    historyScope,
    isLoadingConversation,
    sendMessage,
    continuePausedTurn,
    reviewAcceptedPlay,
    retryMessage,
    confirmAction,
    rejectAction,
    cancelStream,
    clearChat,
    fetchConversations,
    loadConversation,
    deleteConversation,
    snapshotChat,
    replaceChat,
    setModelPick,
  };
};
